/**
 * @memberof SMART2
 * @ngdoc directive
 * @name Textfield
 * @description This directive is useful for creating a textfield or a date-picker or a auto-complete ui element depending upon the 'type' passed.
 * 
 * @attr {String} label
 *    Label to be displayed for identification of this ui element
 * @attr {String} placeholder
 *    Hint text to be displayed in textfield
 * @attr {String} align
 *    Text alignment. Possible values are left, center, right.
 * @attr {Boolean} autocomplete 
 *    This attribute is useful for turning off browser's default autocomplete behaviour
 * @attr {Number} maxlength
 *    Number of characters allowed in textfield
 * @attr {Number} decimalprecision
 *    Number of characters allowed after decimal precision in textfield
 * @attr {Number} min
 *    Minimum value allowed in textfield 
 * @attr {Number} max
 *    Maximum value allowed in textfield
 * @attr {Number} minmaxprecision
 *    Number of characters that should be visible in number type textfield. Internally filter is applied for the given numbers. Data to be sent in array format with 2 array contents 
 * @attr {Var} ng-model
 *    Default value to be displayed in textfield
 * @attr {Object} ng-model-options
 *    https://docs.angularjs.org/api/ng/directive/ngModelOptions
 * @attr {Boolean} focus 
 *    If set to true, this ui element will be focused
 * @attr {Boolean} validate
 *    If set to true, this ui element will be validated on the basis of rules passed to it
 * @attr {Array} rules 
 *    Rules to be evaluated when this element's blur event is fired. A rule must have 'rule' and 'errorMessage' properties (keys).
 *    'rule' must be a condition or group of conditions. 'errorMessage' will be the message to be displayed when corresponding rule fails. See example for more.
 * @attr {String} error-message 
 *    Error message to be displayed. This attribute can be set at any point to display an error message.
 * @attr {Boolean} is-mandatory 
 *    If set to true, default error message will be displayed when blur event is fired and this ui element is left blank
 * @attr {Boolean} is-visible 
 *    If set to true, this ui element will be displayed on form regardless of 'is-mandatory' property's value. 
 *    This ui element can be removed from form by clicking 'x' button on top right corner of this ui element.
 * @attr {Number} colspan
 *    Number of columns to be occupied by this ui element. 1 column is equal to 1 ui element.
 *    Default value is 1.
 *    For e.g. If colspan is set to 2, this ui element will occupy width of 2 ui elements (fields).
 * @attr {String} type 
 *    Type of textfield. Default value is 'text'. Possible values are area (textarea), number, email, date, autocomplete.
 * @attr {Boolean} readonly 
 *    If set to true, textfield cannot be editable. Textfield's blur and focus events will still be fired (default behaviour).
 * @attr {Boolean} disable 
 *    If set to true, textfield cannot be editable. Textfield's blur and focus events will not be fired (default behaviour).
 * @attr {Function} on-focus
 *    Callback function when ui element's focus event is fired
 * @attr {Function} on-blur
 *    Callback function when ui element's blur event is fired
 * @attr {Function} on-key-up
 *    Callback function when ui element's keyup event is fired
 * @attr {Function} on-key-down
 *    Callback function when ui element's keydown event is fired
 * @attr {Function} on-key-press
 *    Callback function when ui element's keypress event is fired
 * @attr {Function} on-enter
 *    Callback function when ENTER (RETURN) key is pressed
 * 
 * @example
 Dynamic:
 Controller:
     $scope.config = {
          "modelData": {
              "companyName": "GEP"
          }, 
          "formConfig": {
              "sections": [
                  {
                      "isMandatory": true,
                      "rows": [
                          {
                              "properties": [
                                  {
                                      "label": "Company name",
                                      "type": "textfield",
                                      "isMandatory": true,
                                      "isVisible": false,
                                      "data": "companyName",
                                      "colspan": 1,
                                      "validate": false,
                                      "focus": false,
                                      "onFocus": "onFocus",
                                      "onBlur": "onBlur" 
                                      "onChange": "onChange"
                                      "onKeyPress": "onKeyPress"
                                      "onKeyUp": "onKeyUp"
                                      "onKeyDown": "onKeyDown",
                                      "attributes": {
                                          "type": "number"
                                          "maxlength": "",
                                          "decimalprecision": "",
                                          "minmaxprecision": [2,5],
                                          "disable": true,
                                          "readonly": true,
                                          "align": "left",
                                          "placeholder": "",
                                          "autocomplete": false
                                      },
                                      "rules": [
                                          { 
                                              "rule": "this.length > 10", 
                                              "errorMessage": "Length should not be greater than 10" 
                                          }
                                      ]
                                  }
                              ]    
                          }
                      ]
                  }
              ]
          }
      };
  Usage:
      <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>

 * @example
 Textfield with ng-model-options. This example is applicable only if one wants to use 'ng-model-options' with 'getterSetter' option. Otherwise VM
 does not need to be used as 'ng-model' or 'data'.
 Dynamic:
 Controller:
     var defaultCompanyName = 'GeP';
     this.companyName = function (val) {
         if (val !== undefined) {
             defaultCompanyName = val;
         }
         return defaultCompanyName;
     };

     $scope.config = {
          "modelData": {
              "companyName": "GEP"
          }, 
          "formConfig": {
              "sections": [
                  {
                      "isMandatory": true,
                      "rows": [
                          {
                              "properties": [
                                  {
                                      "label": "Company name",
                                      "type": "textfield",
                                      "isMandatory": true,
                                      "isVisible": false,
                                      "data": "controllerVM.companyName",
                                      "attributes": {
                                          "ngModelOptions": { getterSetter: true }
                                      },
                                      "rules": [
                                          { 
                                              "rule": "this.length > 10", 
                                              "errorMessage": "Length should not be greater than 10" 
                                          }
                                      ]
                                  }
                              ]    
                          }
                      ]
                  }
              ]
          }
      };
  Usage:
      <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>

 * @example
 Textfield with ng-model-options. This example is applicable only if one wants to use 'ng-model-options' with 'getterSetter' option. Otherwise VM
 does not need to be used as 'ng-model' or 'data'.
 Static:
 Controller:
     angular.module('SMART2', []).controller('textFieldController', ['$scope', function($scope) {
         var defaultCompanyName = 'GeP';
         this.companyName = function (val) {
             if (val !== undefined) {
                 defaultCompanyName = val;
             }
             return defaultCompanyName;
         };
     }]);
  Usage:
      <div ng-controller="textFieldController as textFieldVM">
        <smart-textfield ng-model="textFieldVM.companyName" ng-model-options="{getterSetter: true}"></smart-textfield>
      </div>

 * @example
 Static:
 Controller:
      $scope.companyName = "GEP";
 Usage:
      <smart-textfield label="First name" ng-model="companyName"></smart-textfield>
      
 * @example
 Textfield with events: 
 '$event' passed in callback functions in following example is angular event. 
  <smart-textfield on-focus="onFocus()" 
                   on-blur="onBlur()" 
                   on-change="onChange($event)"
                   on-key-press="onKeyPress($event)"
                   on-key-up="onKeyUp($event)"
                   on-key-down="onKeyDown($event)"></smart-textfield>        
 */


/**
 * @memberof SMART2
 * @ngdoc directive
 * @name Date-picker
 * @description This directive is useful for creating a date picker. This directive is derived from 'textfield' directive. All the attributes of 'textfield' directive are applicable to this directive. Following are some additional attributes.
 * 
 * @attr {String} format 
 *    Date format. For e.g. dd/MM/yyyy. For more details browse https://docs.angularjs.org/api/ng/filter/date
 * @attr {Var} ng-model
 *    Default date to be displayed. 'ng-model' is expected to be in milliseconds (timestamp) or javascript date. For e.g. 5656534324368 or "2014-09-18T10:03:58" or "Fri Mar 04 2016 13:10:56 GMT+0530 (IST)"
 * 
 * @example
 Dynamic: (Timestamp)
 Controller:
     $scope.config = {
          "modelData": {
              "date": 1457094173954
          }, 
          "formConfig": {
              "sections": [
                  {
                      "isMandatory": true,
                      "rows": [
                          {
                              "properties": [
                                  {
                                      "label": "Date",
                                      "type": "textfield",
                                      "isMandatory": true,
                                      "data": "date",
                                      "attributes": {
                                          "type": "date",
                                          "format": "dd/MM/yyyy"
                                      }
                                  }
                              ]    
                          }
                      ]
                  }
              ]
          }
      };
  Usage:
      <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>

 * @example
 Dynamic: (Javascript date)
 Controller:
     $scope.config = {
          "modelData": {
              "date": "Fri Mar 04 2016 17:50:50 GMT+0530 (IST)"
          }, 
          "formConfig": {
              "sections": [
                  {
                      "isMandatory": true,
                      "rows": [
                          {
                              "properties": [
                                  {
                                      "label": "Date",
                                      "type": "textfield",
                                      "isMandatory": true,
                                      "data": "date",
                                      "attributes": {
                                          "type": "date",
                                          "format": "dd/MM/yyyy"
                                      }
                                  }
                              ]    
                          }
                      ]
                  }
              ]
          }
      };
  Usage:
      <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>

 * @example
 Static: (Timestamp)
 Controller:
      $scope.date = 1457094173954;
 Usage:
      <smart-textfield type="date" label="Date" ng-model="date"><smart-textfield> 
      
 * @example
 Static: (Javascript date)
 Controller:
      $scope.date = "Fri Mar 04 2016 17:50:50 GMT+0530 (IST)";
 Usage:
      <smart-textfield type="date" label="Date" ng-model="date"><smart-textfield>         
 */


/**
 * @memberof SMART2
 * @ngdoc directive
 * @name Auto-complete
 * @description This directive is useful for creating an auto-complete ui element. This directive is derived from 'textfield' directive. All the attributes of 'textfield' directive are applicable to this directive. Following are some additional attributes.
 * 
 * @attr {Object} ng-model
 *    Default value to be displayed in textfield. 'ng-model' is expected to be an object having property (key) set in 'datakey' attribute.
 * @attr {Array} filterkeys
 *    An array of properties (keys) on which filtering to be applied. Suggestions will get filtered on the basis of properties (keys) passed in array.
 * @attr {String} optionformat
 *    A single suggestion format (template)
 * @attr {String} displayformat
 *    A format in which the data in textfield to be displayed
 * @attr {Boolean} loading 
 *    This attribute can be used to display a loader while autosuggest data is being fetched from the server.
 * @attr {Function} on-select
 *    Callback when a suggestion is selected
 * 
 * @example
 Dynamic:
 Controller:
     $scope.onChange = function(uiElementConfig) {
          uiElementConfig.attributes.options = [
              {
                  "UserId": 28360,
                  "UserName": "SRUser1@outlook.com",
                  "FirstName": "SR ",
                  "LastName": "User1"
              }, {
                  "UserId": 28977,
                  "UserName": "test1",
                  "FirstName": "Test",
                  "LastName": ""
              }, {
                  "UserId": 57950,
                  "UserName": "HShah",
                  "FirstName": "Harshit",
                  "LastName": "Shah"
              }
          ];
     };
     $scope.onSelect = function(autoSuggestData) {
         console.log(autoSuggestData);
         //   autoSuggestData will be updated every time a new suggestion is chosen. Passing 'model' in 'onSelect' is optional.
     };
     $scope.config = {
          "modelData": {
              "autoSuggestData": { "UserId": 28360, "UserName": "SRUser1@outlook.com", "FirstName": "SR", "LastName": "User1", "title": "SR User1" }
          }, 
          "formConfig": {
              "sections": [
                  {
                      "isMandatory": true,
                      "rows": [
                          {
                              "properties": [
                                  {
                                      "label": "Auto complete",
                                      "type": "textfield",
                                      "isMandatory": true,
                                      "data": "autoSuggestData",
                                      "onChange": "onChange",
                                      "onSelect": "onSelect(autoSuggestData)",
                                      "attributes": {
                                          "type": "autocomplete",
                                          "options": [],
                                          "filterkeys": ["FirstName", "LastName"],
                                          "optionformat": "<span>{FirstName} {LastName}</span>",
                                          "displayformat": "{FirstName} {LastName}",
                                          "loading": false
                                      }
                                  }
                              ]    
                          }
                      ]
                  }
              ]
          }
     };
  Usage:
     <smart-form-widget form-config="config.formConfig" model-data="config.modelData"></smart-form-widget>
 * @example
 Static:
 Controller:
     $scope.autoCompleteData = { "UserId": 28360, "UserName": "SRUser1@outlook.com", "FirstName": "SR", "LastName": "User1", "title": "SR User1" };
     $scope.onChange = function(e) {
          //  Textfield's value can be found in e.data[0].value
          //  See console for more
          console.log(e);
          $scope.options = [
              {
                  "UserId": 28360,
                  "UserName": "SRUser1@outlook.com",
                  "FirstName": "SR ",
                  "LastName": "User1"
              }, {
                  "UserId": 28977,
                  "UserName": "test1",
                  "FirstName": "Test",
                  "LastName": ""
              }, {
                  "UserId": 57950,
                  "UserName": "HShah",
                  "FirstName": "Harshit",
                  "LastName": "Shah"
              }
          ]; 
     };
     $scope.onSelect = function(autoCompleteData) {
         console.log(autoCompleteData);
         //   autoCompleteData will be updated every time a new suggestion is chosen. Passing 'model' in 'onSelect' is optional.
     };
 Usage:
     <smart-textfield type="autocomplete" label="Auto complete" ng-model="autoCompleteData" filterkeys="['FirstName', 'LastName']" optionformat="<span>{FirstName} {LastName}</span>" displayformat="{FirstName} {LastName}" options="options" on-change="onChange($event)" on-select="onSelect(autoCompleteData)"></smart-textfield>
 */


(function () {
    'use strict';
    var textfieldId = 19820116;
    //var directiveV2 = true;
    //var isDirectiveV2enabled = function () { return true; }
    $(function () {
        angular.element('body').off('click').on('click', '.daterangepicker', function (e) {
            e.stopPropagation();
        });
    });

    angular.module('SMART2').directive('smartTextfield', ['$rootScope', '$timeout', '$translate', 'ScrollTo', 'RuleEngine', 'smartTextFieldService', 'smartTextField3Service', '$filter', '$window',
        (typeof isDirectiveV2enabled !== 'undefined' ? (isDirectiveV2enabled() === true || isDirectiveV2enabled() === 'V3' ? smartTextField3Func : smartTextFieldFunc) : smartTextFieldFunc)]);


    function smartTextFieldFunc($rootScope, $timeout, $translate, ScrollTo, RuleEngine, $window) {
        return {
            restrict: 'E',
            replace: true,
            require: '?ngModel',
            scope: {
                label: '@',
                //ngModel: '=?',
                modelOptions: '=?',
                options: '=?',
                isMandatory: '@',
                isVisible: '=?',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                onChange: '&',
                onFocus: '&',
                onBlur: '&',
                onKeyUp: '&',
                onKeyDown: '&',
                onKeyPress: '&',
                onEnter: '&',
                onSelect: '&',
                onDestroy: '&',
                onDateChange: '&',
                errorMessage: '@',
                errorModel: '=?',
                setFromGrid: '@',
                //  input element's attribute except for listeners should be in small letters
                type: '@',
                align: '@',
                datanumeric: '@',
                spinner: '@',
                step: '@',
                format: '@',    //  date format
                timePicker: '@', //boolean value if date with time picker
                isValueDateObj: '@',
                readonly: '@',
                disable: '@',
                prefixicon: '@',
                suffixicon: '@',
                maxlength: '@',
                min: '@',
                max: '@',
                timePicker12Hour: '@',
                minuteIncrement: '@',
                charactercounter: '@',
                decimalprecision: '@',
                minmaxprecision: '@',
                placeholder: '@',
                autocomplete: '@',
                removable: '@',
                datakey: '@',
                filterkeys: '@',
                optionformat: '@',
                displayformat: '@',
                width: '@',      //  autosuggest container width
                minchars: '@',   //  autosuggest min chars
                loading: '@',     //  loader while autosuggest data is being fetched
                count: '@',
                noSuggestionNotice: '@',
                showNoSuggestion: '@',
                clearListOnFocus: '@',
                retainDisplayModelAutocomplete: '@',
                showinfo: '@',
                showwarning: '@'
            },
            link: function (scope, element, attrs, ngModelCtrl) {
                scope.modelOptions = scope.modelOptions === false ? {} : !scope.modelOptions ? {
                    updateOn: 'default blur change',
                    debounce: { 'default': 300, 'blur': 0, 'change': 0 }
                } : scope.modelOptions;
                scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
                scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
                scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
                scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon
                var timePicker = (typeof scope.timePicker == undefined ? false : scope.timePicker == 'true' ? true : false);
                scope.format = (scope.format ? scope.format : timePicker ? 'DD/MM/YYYY h:mm' : 'DD/MM/YYYY');
                scope.count = 0;
                var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
                var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
                showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
                var number, rulesValidationEvent;
                scope.type = angular.isDefined(scope.type) ? scope.type : 'text';


                if (scope.type == 'number') {
                    element.bind("DOMMouseScroll mousewheel onmousewheel", function (event) {

                        // for IE
                        event.returnValue = false;
                        // for Chrome and Firefox
                        if (event.preventDefault) {
                            event.preventDefault();
                        }

                    });
                }


                var uniqueIDGenerator = function () {
                    var d = new Date().getTime();
                    var uniqueID = 'xx2xxpxxoxx'.replace(/[xy]/g, function (c) {
                        var r = (d + Math.random() * 16) % 16 | 0;
                        d = Math.floor(d / 16);
                        return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
                    });
                    return uniqueID;
                };

                if (attrs.validateOn) {
                    rulesValidationEvent = attrs.validateOn;
                }
                else {
                    rulesValidationEvent = 'blur';
                }


                /*
                 *  Following code has been added to make ng-model-options work
                 *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
                 */
                try {
                    ngModelCtrl.$render = function () {
                        scope.ngModel = ngModelCtrl.$modelValue;
                    };
                }
                catch (e) { }

                var updateView = function () {
                    try {
                        ngModelCtrl.$setViewValue(scope.ngModel);
                    }
                    catch (e) { }
                };


                function checkIfActive() {
                    if (scope.readonly == 'true') {
                        return true;
                    }
                    if (scope.type == 'number' && scope.ngModel !== 0 + '' && scope.placeholder === '' && (scope.ngModel === '' || isNaN(scope.ngModel) || scope.ngModel === undefined || scope.ngModel === null)) {
                        return false;
                    }
                    if (scope.type != 'number' && scope.type != 'autocomplete' && scope.placeholder === '' && (scope.ngModel === '' || scope.ngModel === undefined || scope.ngModel === null)) {
                        return false;
                    }
                    if (scope.type == 'autocomplete' && scope.placeholder === '' && (scope.displayModel === '' || scope.displayModel === undefined || scope.displayModel === null)) {
                        return false;
                    }
                    return true;
                }


                function setPrecisionLimits() {
                    number = scope.ngModel;
                    if (number != undefined) {
                        var arr = (number.toString()).split('e')
                        if (arr.length > 1 && parseInt(arr[1]) < 0) {
                            number = (number).toFixed(6);
                            scope.ngModel = number;
                        }
                        var tempArr = number.toString().split('.');
                        if (tempArr != undefined && tempArr.length > 1) {
                            var preDecimal = tempArr[0];
                            if (preDecimal != 0 && preDecimal.toString().length > 20) {
                                var tempVal = preDecimal.toString().substring(0, 19);
                                scope.ngModel = parseInt(tempVal);
                            }
                        }
                        else {
                            if (number != 0 && number.toString().length > 20) {
                                var tempVal = number.toString().substring(0, 19);
                                scope.ngModel = parseInt(tempVal);
                            }
                        }
                        if (scope.decimalprecision != undefined) {
                            scope.ngModel = parseFloat(parseFloat(scope.ngModel).toFixed(scope.decimalprecision));
                        }
                        else if (scope.minmaxprecision !== undefined && scope.minmaxprecision !== '') {
                            var splitPrecision = JSON.parse(scope.minmaxprecision);
                            if (splitPrecision.length > 2 || splitPrecision.length < 1)
                                return;
                            var lowerPrecisionLimit = splitPrecision[0];
                            var highPrecisionLimit = splitPrecision[1];

                            var postDecimalNumbers = ((scope.ngModel + "").split(".")[1] != undefined) ? (scope.ngModel + "").split(".")[1].length : 0;

                            if (postDecimalNumbers == 0)
                                return;

                            if (lowerPrecisionLimit > postDecimalNumbers && postDecimalNumbers < highPrecisionLimit && postDecimalNumbers != 0) {
                                scope.ngModel = parseFloat(parseFloat(scope.ngModel).toFixed(lowerPrecisionLimit));
                            }
                            else if (postDecimalNumbers >= highPrecisionLimit && postDecimalNumbers != 0) {
                                var fixed = Math.pow(10, highPrecisionLimit);
                                scope.ngModel = Math.floor(scope.ngModel * fixed) / fixed;
                            }
                        }
                    }
                }

                scope.isActive = checkIfActive();

                scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

                if (attrs.id != undefined || attrs.id != null) {
                    scope.textfieldId = attrs.id;
                    element.removeAttr('id');
                }
                else {
                    textfieldId = uniqueIDGenerator();
                    scope.textfieldId = 'txt-' + textfieldId;
                }

                scope.labelForTextfieldId = 'label-for-' + scope.textfieldId;

                /*
                * Skip tab key focus, if the field is readonly
                */
                setTimeout(function () {
                    if (scope.readonly == 'true' && scope.type != 'date') {
                        angular.element('#' + scope.textfieldId).attr('tabindex', -1);
                    }

                    try {
                        if (angular.isDefined(scope.$parent.$parent.onEditableRender)) {
                            scope.$parent.$parent.onEditableRender(scope, element);
                        }
                    }
                    catch (e) { }
                });

                /*
                 * Validate the textfield as soon as the validate is set to true
                 */
                var unbindValidate = scope.$watch('validate', function (newVal, oldVal) {
                    if (newVal) {
                        scope.validateRules();
                    }
                });

                /*
                 * Focus the textfield as soon as the focus is set to true
                 */
                var unbindFocus = scope.$watch('focus', function (newVal, oldVal) {
                    if (newVal) {
                        ScrollTo.perform(element, angular.element('#' + scope.parentElement));
                        //  settimeout is added to avoid $digest already in progress
                        setTimeout(function () {
                            var el = angular.element('#' + scope.textfieldId);
                            if (scope.label) {
                                el.trigger('click');
                                el.focus();
                            } else {
                                // if label is undefined or empty then directly focus on input element 
                                if (el.is("input")) {
                                    el.focus();
                                } else {
                                    el.find("input").focus();
                                }
                            }
                        }, 100); // increased timer hotfix for focus cell in ui-grid
                        scope.focus = false;
                    }
                });
                var checkRule = function () {
                    var rules = scope.$eval(scope.rules);
                    var isFoundInvalid = false;

                    for (var i = 0; i < rules.length; i++) {
                        if (typeof rules[i] == 'object') {
                            if (eval((rules[i].rule).replace(/this/g, scope.type == 'autocomplete' ? 'scope.displayModel' : 'scope.ngModel'))) {
                                scope.validate = true;
                                scope.errorMessage = rules[i].error;
                                scope.errorModel = rules[i].error;
                                break;
                            } else {
                                if (scope.type === 'date') {
                                    angular.element('#' + scope.textfieldId).removeClass('invalid');
                                }
                            }
                        }
                        else {
                            if (!isFoundInvalid) {
                                if (typeof scope.ngModel != 'object')
                                    var data = scope.ngModel;
                                else
                                    var data = scope.displayModel;

                                RuleEngine.isValid(rules[i], function (e) {
                                    scope.validate = true;
                                    scope.errorMessage = e.errorData.error;
                                    scope.errorModel = e.errorData.error;
                                    isFoundInvalid = true;
                                }, data);
                            }
                            else {
                                break;
                            }
                        }
                    }
                }
                /*
                 * Validate textfield rules
                 */
                scope.validateRules = function () {
                    if (scope.readonly == 'true') {
                        scope.isActive = true;
                        return;
                    }

                    if ((scope.type === 'text' || scope.type === 'area') && angular.isString(scope.ngModel)) {
                        if (attrs.noTrim == 'true') {
                            scope.ngModel = scope.ngModel;
                        } else {
                            scope.ngModel = scope.ngModel.trim();
                        }
                        ngModelCtrl.$setViewValue(scope.ngModel);
                    }

                    if ((scope.type == 'autocomplete' && scope.$eval(scope.isMandatory) && (scope.displayModel == '' || scope.displayModel == null || scope.displayModel == undefined || scope.ngModel.id === 0)) || (scope.type != 'autocomplete' && scope.$eval(scope.isMandatory) && (scope.ngModel + '' == '' || scope.ngModel == null || scope.ngModel == undefined))) {
                        scope.validate = true;
                        scope.errorMessage = $translate.instant('enterValueErrMsg');
                        scope.errorModel = $translate.instant('enterValueErrMsg');
                        return;
                    }

                    if (scope.type == 'number') {
                        if (!scope.$eval(scope.isMandatory) && isNaN(scope.ngModel)) {
                            angular.element('#' + scope.textfieldId).val('');
                            return;
                        }
                        if (!scope.$eval(scope.isMandatory) && scope.ngModel === null) {
                            if (scope.rules) {
                                checkRule();
                            }
                            return;
                        }
                        if (scope.$eval(scope.isMandatory) && isNaN(scope.ngModel) || (!scope.$eval(scope.isMandatory) && number == undefined)) {
                            scope.validate = true;
                            scope.errorMessage = $translate.instant('enterNumberErrMsg');
                            scope.errorModel = $translate.instant('enterNumberErrMsg');
                            return;
                        }
                    }

                    if (scope.type == 'date' && !scope.rules) {
                        $('#' + scope.textfieldId).removeClass('invalid');
                    }

                    scope.validate = false;
                    scope.errorMessage = null;
                    scope.errorModel = null;

                    if (scope.rules) {
                        var rules = scope.$eval(scope.rules);
                        var isFoundInvalid = false;

                        for (var i = 0; i < rules.length; i++) {
                            if (typeof rules[i] == 'object') {
                                if (eval((rules[i].rule).replace(/this/g, scope.type == 'autocomplete' ? 'scope.displayModel' : 'scope.ngModel'))) {
                                    scope.validate = true;
                                    scope.errorMessage = rules[i].error;
                                    break;
                                }
                            }
                            else {
                                if (!isFoundInvalid) {
                                    if (typeof scope.ngModel != 'object')
                                        var data = scope.ngModel;
                                    else
                                        var data = scope.displayModel;

                                    RuleEngine.isValid(rules[i], function (e) {
                                        scope.validate = true;
                                        scope.errorMessage = e.errorData.error;
                                        isFoundInvalid = true;
                                    }, data);
                                }
                                else {
                                    break;
                                }
                            }
                        }
                    }
                };


                //  callback events
                var isDateBlank = false;
                scope.ngChange = function (e) {
                    if (scope.type == 'number') {
                        if (scope.ngModel == null || scope.ngModel == undefined)
                            scope.count = 0;
                        setPrecisionLimits();
                    }
                    scope.isActive = checkIfActive();
                    if (rulesValidationEvent == 'change') {
                        scope.validateRules();
                    }

                    if (scope.type != 'autocomplete') {
                        updateView();
                    }

                    //  Value of 'e' is undefined in ng-change
                    scope.onChange({
                        $event: {
                            data: angular.element('#' + scope.textfieldId),
                            displayModel: scope.displayModel
                        }
                    });
                };

                scope.ngFocus = function (e) {
                    if (scope.type === 'autocomplete' || scope.clearListOnFocus === "true") {
                        var Obj = element.find('input').devbridgeAutocomplete();
                        if (element.find('input').val() == '' && Obj) {
                            Obj.clear();
                        }
                    }
                    scope.isActive = true;
                    scope.validate = false;
                    if (rulesValidationEvent == 'focus') {
                        scope.validateRules();
                    }
                    scope.onFocus({ $event: e });
                };

                scope.ngBlur = function (e) {
                    if (scope.type == 'number') {
                        if (scope.min != undefined || scope.min != null) {
                            if (scope.ngModel == undefined || scope.ngModel == null || isNaN(scope.ngModel)) {
                                $('#' + scope.textfieldId).val('');
                            }
                        }
                    }
                    if (scope.type == 'date' && isDateBlank) {
                        $timeout(function () {
                            var targetedEle = document.activeElement;
                            if (!(angular.element(targetedEle).hasClass('monthselect') || angular.element(targetedEle).hasClass('yearselect') || angular.element(targetedEle).hasClass('hourselect') || angular.element(targetedEle).hasClass('minuteselect'))) {
                                //  scope.ngModel = null;
                                scope.dateModel = null;
                            }
                            if (angular.isFunction(scope.onDateChange)) {
                                scope.onDateChange({
                                    $date: scope.ngModel
                                });
                            }
                        });
                    }
                    if (scope.type === 'autocomplete') {
                        if (scope.ngModel && scope.displayformat) {
                            var splittedDisplayFormat = [];
                            if (scope.displayformat.indexOf(":") > 0)
                                splittedDisplayFormat = scope.displayformat.split(':');
                            else if (scope.displayformat.indexOf("-") > 0)
                                splittedDisplayFormat = scope.displayformat.split('-');
                            else
                                splittedDisplayFormat = scope.displayformat.split('-');
                            var result = "";
                            for (var i = 0; i < splittedDisplayFormat.length; i++) {
                                result += eval("scope.ngModel." + splittedDisplayFormat[i].replace('{', '').replace('}', ''));
                                if (i < splittedDisplayFormat.length - 1) {
                                    if (scope.displayformat.indexOf("-") > 0)
                                        result += '-';
                                    else if (scope.displayformat.indexOf(":") > 0)
                                        result += ':';
                                }
                            }
                            if (scope.retainDisplayModelAutocomplete !== "true") {
                                if (result !== scope.displayModel)
                                    scope.displayModel = '';
                            }
                        }
                    }
                    //as debounce is used smartTextField templates for updating model, $timeout is used to keep validateRules() on blur in sync
                    $timeout(function () {
                        if (scope.type == 'date') {
                            if (pickerObj && pickerObj.isShowing) {
                                return;
                            }
                            else {
                                scope.validateRules();
                                scope.isActive = checkIfActive();
                            }
                        }
                        else {
                            scope.validateRules();
                            scope.isActive = checkIfActive();

                            try {
                                if (angular.isDefined(scope.$parent.$parent.onStopEdit)) {
                                    scope.$parent.$parent.onStopEdit(scope, element);
                                }
                            }
                            catch (e) { }
                        }
                    })

                    //  had to use jQuery because 'active' class does not get removed sometimes even if scope.isActive's value is false
                    scope.isActive || scope.validate ? angular.element('#' + scope.labelForTextfieldId).addClass('active') : angular.element('#' + scope.labelForTextfieldId).removeClass('active');

                    //  had to use jQuery because 'invalid' class does not get removed sometimes even if scope.validate's value is false
                    if (attrs.charactercounter) {
                        scope.validate && scope.errorMessage ? angular.element('#' + scope.textfieldId).addClass('invalid') : angular.element('#' + scope.textfieldId).removeClass('invalid');
                    }
                    scope.focus = false;
                    scope.onBlur({ $event: e });
                };

                scope.ngKeyUp = function (e) {
                    if (scope.type == 'date') {
                        if (e.which != 32) {
                            var currentInputValue = $('#' + scope.textfieldId).val();
                            if (currentInputValue == "") {
                                scope.ngModel = null;
                                scope.dateModel = "";
                                isDateBlank = true;
                                ngModelCtrl.$setViewValue('');
                                if (angular.isFunction(scope.onDateChange)) {
                                    scope.onDateChange({
                                        $date: scope.ngModel
                                    });
                                }
                            }
                        }
                    }
                    if (rulesValidationEvent == 'keyUp') {
                        scope.validateRules();
                    }
                    scope.onKeyUp({ $event: e });
                };

                scope.ngKeyDown = function (e) {
                    if (rulesValidationEvent == 'keyDown') {
                        scope.validateRules();
                    }
                    //to control number of down arrow,up arrow signs on number fields
                    if (scope.type === "number" && (e.keyCode == 38 || e.keyCode == 40))
                        e.preventDefault();
                    scope.onKeyDown({ $event: e });

                };

                scope.ngKeyPress = function (e) {
                    if (scope.type == 'number' || attrs.datanumeric) {
                        //to control number of +,-, signs and alphabets on number fields
                        var charCode = e.charCode || e.keyCode,
                        textValue = e.currentTarget.value,
                        decimalDigit = [],
                        cc = String.fromCharCode(charCode),
                        escapeChars = [37, 38, 39, 40, 46, 8];

                        if (isNaN(cc) && (escapeChars.indexOf(charCode) == -1)) {
                            e.preventDefault();
                        }
                        // Restrict the 2 dots entering
                        if (textValue.toString().indexOf('.') !== -1 && charCode == 46) {
                            e.preventDefault();
                        }

                        if (textValue) {
                            decimalDigit = textValue.toString().split('.');
                        }
                        // Restrict the decimal value from entering
                        if (attrs.decimalprecision) {
                            if (charCode == 46 && parseInt(attrs.decimalprecision) == 0) {
                                e.preventDefault();
                            }
                            if (decimalDigit.length == 2) {
                                if (decimalDigit[1] && decimalDigit[1].length >= parseInt(attrs.decimalprecision, 10)) {
                                    e.preventDefault();
                                }
                            }
                        }
                        if (scope.min != undefined || scope.min != null) {
                            if (scope.min >= 0 && e.charCode == 45) {
                                e.preventDefault();
                            }
                        }
                    }

                    if (rulesValidationEvent == 'keyPress') {
                        scope.validateRules();
                    }
                    scope.onKeyPress({ $event: e });
                    if (e.charCode == 13) {
                        scope.onEnter({ $event: e });
                    }

                    // because of debounce has applied on smart-textfield, here we need to do DOM manipulation i.e. e.target.value to get latest value from textfield
                    if (e.target.value.length === attrs.maxlength && e.charCode !== 0) {
                        e.preventDefault();
                    }
                };


                /*
                 * This function is required to generate value attribute 
                 * and useful when up/down key is pressed
                 */
                function getFormattedData(data) {
                    if (!data || data.length == 0) {
                        return [];
                    }
                    for (var i = 0; i < data.length; i++) {
                        var str = displayFormat;
                        try {
                            var parentSplit = str.split('{');
                            for (var j = 0; j < parentSplit.length; j++) {
                                var childSplit = parentSplit[j].split('}');
                                for (var k = 0; k < childSplit.length; k++) {
                                    if (childSplit[k].indexOf('.') > 0 || data[i].hasOwnProperty(childSplit[k])) {
                                        str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), eval('data[i].' + childSplit[k]));
                                    }
                                }
                            }
                        }
                        catch (e) { }
                        data[i].value = str.trim();
                    }
                    return data;
                };


                /*
                 * Get plain display data
                 */
                function getDisplayData(data) {
                    if (data == undefined || data == null || data == '') {
                        return '';
                    }
                    var str = displayFormat, evalResult, evaluatedKeysCounter = 0, nullKeysCounter = 0;
                    try {
                        var parentSplit = str.split('{');
                        for (var j = 0; j < parentSplit.length; j++) {
                            var childSplit = parentSplit[j].split('}');
                            for (var k = 0; k < childSplit.length; k++) {
                                if (childSplit[k].indexOf('.') > 0 || data.hasOwnProperty(childSplit[k])) {
                                    evalResult = eval('data.' + childSplit[k]);
                                    evaluatedKeysCounter++;
                                    if (evalResult == undefined || evalResult == null || (evalResult + '').trim().length == 0) {
                                        evalResult = '';
                                        nullKeysCounter++;
                                    }
                                    str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), evalResult);
                                }
                            }
                        }
                        if (evaluatedKeysCounter == nullKeysCounter) {
                            str = '';
                        }
                    }
                    catch (e) { }
                    return str === undefined || str === null || str === 'undefined' || str === 'null' ? '' : str;
                }


                /*
                 *  Convert timestamp to UTC timestamp 
                 */
                function toUTC(date) {
                    return Date.UTC(
                        date.getFullYear()
                        , date.getMonth()
                        , date.getDate()
                        , date.getHours()
                        , date.getMinutes()
                        , date.getSeconds()
                        , date.getMilliseconds()
                    );
                };
                scope.isValueDateObj = false;
                var doesContainDateString = false;
                var dateFormate = scope.format;
                function getGridFormatToTimestamp(stringdate) {
                    var tmpModel = (stringdate + '').replace(new RegExp("/", "ig"), '');
                    // tmpModel = tmpModel.split('+');
                    // tmpModel = eval('new ' + (tmpModel.length > 1 ? ('Date("' + tmpModel.join('+') + '").getTime()') : tmpModel[0]));
                    return moment(tmpModel).format(dateFormate);
                }
                var pickerObj;
                function createDatePicker() {
                    $timeout(function () {
                        var picker = angular.element('#' + scope.textfieldId);
                        var callApplyFuncFlag = true;
                        function setDateFormate(datePar) {
                            if (doesContainDateString) {
                                scope.ngModel = '\/Date(' + (datePar.unix() * 1000) + ')\/';
                            } else {
                                if (scope.isValueDateObj) {
                                    var tempDate = datePar.format();
                                    scope.ngModel = new Date(tempDate);
                                } else {
                                    scope.ngModel = (datePar.unix() * 1000);
                                }
                            }
                            ngModelCtrl.$setViewValue(scope.ngModel);
                            isDateBlank = false;
                            if (angular.isFunction(scope.onDateChange)) {
                                scope.onDateChange({ $date: scope.ngModel });
                            }
                        }
                        var minDate = angular.isDefined(scope.min) ? doesContainDateString ? getGridFormatToTimestamp(scope.min) : moment(parseInt(scope.min)).format(dateFormate) : '';
                        var maxDate = angular.isDefined(scope.max) ? doesContainDateString ? getGridFormatToTimestamp(scope.max) : moment(parseInt(scope.max)).format(dateFormate) : '';

                        var timePicker24Hour = angular.isDefined(scope.timePicker12Hour) ? false : true;
                        var timePickerIncrement = angular.isDefined(scope.minuteIncrement) ? scope.$eval(scope.minuteIncrement) : 1;

                        pickerObj = picker.daterangepicker({
                            "singleDatePicker": true,
                            "showDropdowns": true,
                            "timePicker": timePicker,
                            "timePicker24Hour": timePicker24Hour,
                            "timePickerIncrement": timePickerIncrement,
                            "autoApply": false,
                            "opens": "right",
                            "drops": "down",
                            "minDate": minDate,
                            "maxDate": maxDate,
                            "locale": {
                                "format": dateFormate,
                                'cancelLabel': $translate.instant('cancelBtn'),
                                'applyLabel': $translate.instant('applyBtn')
                            }
                        }, function (start, end, label) {
                            callApplyFuncFlag = false;
                            setDateFormate(end);
                        }).bind("show.daterangepicker", function () { // callback on after open datepicker
                            var pos = pickerObj.container.offset(),
                                wHeight = window.innerHeight,
                                srTop = $(window).scrollTop();
                            if ((pos.top + pickerObj.container.outerHeight() - srTop) > wHeight) { // if datepicker goes down then push it up
                                pickerObj.drops = "up";
                                pickerObj.move();
                            } else if (pos.top < srTop) { // else if datepicker goes up then push it down
                                pickerObj.drops = "down";
                                pickerObj.move();
                            }
                        }).data("daterangepicker");

                        picker.on('apply.daterangepicker', function (ev, pickerr) {
                            if (callApplyFuncFlag) {
                                setDateFormate(pickerr.endDate)
                            }
                            callApplyFuncFlag = true;
                        });

                        if (isDateBlank) {
                            picker.val('');
                            scope.ngModel = "";
                            ngModelCtrl.$setViewValue('');
                        };
                    });
                }

                switch (scope.type) {
                    case 'autocomplete':
                        // key to be used to bind options
                        var filterKeys;
                        if (angular.isDefined(scope.filterkeys) && scope.filterkeys.trim().indexOf('[') == 0) {
                            filterKeys = scope.filterkeys ? scope.$eval(scope.filterkeys) : ['title'];
                        }
                        else {
                            filterKeys = scope.filterkeys ? scope.$eval('[' + scope.filterkeys + ']') : ['title'];
                        }
                        var optionFormat = scope.optionformat ? scope.optionformat : '{title}';
                        var displayFormat = scope.displayformat ? scope.displayformat : '{title}';

                        /*
                         * Check if the options are updated
                         */
                        var autoCompleteField;
                        var unbindOptions = scope.$watch('options', function (newOptions) {
                            try {
                                if (!$('#' + scope.textfieldId).devbridgeAutocomplete()) {
                                    autoCompleteField = $('#' + scope.textfieldId).devbridgeAutocomplete({
                                        filterKeys: filterKeys,
                                        optionFormat: optionFormat,
                                        displayFormat: displayFormat,
                                        lookup: getFormattedData(newOptions),
                                        width: scope.width,
                                        showNoSuggestionNotice: showNoSuggestion,
                                        noSuggestionNotice: noSuggestionNotice,
                                        minChars: angular.isDefined(scope.minchars) ? parseInt(scope.minchars) : 1,
                                        onSelect: function (suggestion) {
                                            angular.element('#' + scope.labelForTextfieldId).trigger('click');
                                            scope.$evalAsync(function () {
                                                scope.displayModel = getDisplayData(suggestion);
                                                scope.ngModel = suggestion;
                                                updateView();
                                                $timeout(function () {
                                                    scope.onSelect({
                                                        $event: {
                                                            data: suggestion
                                                        }
                                                    });
                                                });
                                            });
                                        }
                                    }).devbridgeAutocomplete();
                                }
                                else {
                                    $('#' + scope.textfieldId).devbridgeAutocomplete().setLookup(getFormattedData(newOptions));
                                }
                            }
                            catch (e) { }
                        });

                        /*
                         * Destroy autocomplete on scope destroy
                         */
                        scope.$on('$destroy', function () {
                            if ($('#' + scope.textfieldId).devbridgeAutocomplete()) {
                                $('#' + scope.textfieldId).devbridgeAutocomplete().dispose();
                            }
                            destroyEverythingElse();
                        });
                        break;
                    case 'date':
                        /*
                         * Destroy datepicker on scope destroy
                         */
                        scope.$on('$destroy', function () {
                            angular.element('#' + scope.textfieldId + '_root').remove();
                            destroyEverythingElse();
                        });
                        break;
                    case 'text':
                    case 'area':
                    case 'number':
                        scope.$on('$destroy', function () {
                            destroyEverythingElse();
                        });
                        break;
                }

                //Closing the suggestions on scroll to prevent overlapping issue
                angular.element($window).scroll(function (e) {
                    hideAutoOnScroll();
                });

                angular.element(".modal, .modal-content").scroll(function (e) {
                    hideAutoOnScroll();
                });

                function hideAutoOnScroll() {
                    if (autoCompleteField != undefined) {
                        if (autoCompleteField.visible) {
                            autoCompleteField.hide();
                        }
                    }
                }

                /*
                 *  Update 'active' class when textfield's value is updated
                 */
                switch (scope.type) {
                    case 'autocomplete':
                    case 'text':
                    case 'area':
                    case 'number':
                        var unbindNumber = scope.$watch('ngModel', function (newVal) {
                            if (scope.type == 'autocomplete' && angular.isDefined(newVal)) {
                                scope.displayModel = getDisplayData(newVal);
                                if (scope.displayModel !== "") {
                                    scope.validate = false;
                                    scope.errorMessage = null;
                                    scope.errorModel = null;
                                }
                            }
                            if (scope.type == 'number') {
                                setPrecisionLimits();
                            }
                            if (attrs.decimalprecision && attrs.datanumeric) {
                                setPrecisionLimits();
                            }
                            scope.isActive = checkIfActive();
                            if (rulesValidationEvent == 'change') {
                                scope.validateRules();
                            }
                            if (scope.type == 'area') {
                                $timeout(function () {
                                    angular.element('#' + scope.textfieldId).trigger('autoresize');
                                });
                            }
                        });
                        break;

                    case 'date':
                        var unbindDateModel = scope.$watch('dateModel', function (newVal) {
                            scope.isActive = checkIfActive();
                            if (rulesValidationEvent == 'change') {
                                scope.validateRules();
                            }
                        });
                        var unbindNgModel = scope.$watch('ngModel', function (newVal) {
                            dateFormate = scope.format.replace(/d/g, 'D').replace(/y/g, 'Y').replace(/E/g, 'd');
                            isDateBlank = false;
                            doesContainDateString = scope.setFromGrid == 'true' ? true : (newVal + '').toLowerCase().indexOf('date') > -1 ? true : false;
                            if (newVal == "" || newVal == null) {
                                newVal = Date.now();
                                isDateBlank = true;
                                scope.isValueDateObj = false;
                            } else {
                                if (doesContainDateString) {
                                    var tmpModel = (newVal + '').replace(new RegExp("/", "ig"), '');
                                    tmpModel = tmpModel.split('+');
                                    tmpModel = eval('new ' + (tmpModel.length > 1 ? ('Date("' + newVal + '").getTime()') : tmpModel[0]));
                                    scope.dateModel = moment(tmpModel).format(dateFormate);
                                }
                                else {
                                    if (Object.prototype.toString.call(newVal) === '[object Date]') {
                                        scope.isValueDateObj = true;
                                    } else {
                                        newVal = parseInt(newVal);
                                    }
                                }
                            }

                            //scope.placeholder = scope.format;
                            scope.placeholder = scope.placeholder == undefined || scope.placeholder == null || scope.placeholder == "" ? 'DD/MM/YYYY' : scope.placeholder; // to consider custom placeholder value and not only the format of the date-time picker

                            if (!isDateBlank) {
                                if (!doesContainDateString) {
                                    scope.dateModel = moment(newVal).format(dateFormate);
                                }
                            } else {
                                scope.dateModel = "";
                            }
                            createDatePicker();
                        });
                        var unbindMin = scope.$watch('min', function (n) {
                            createDatePicker();
                        });
                        var unbindMax = scope.$watch('max', function (n) {
                            createDatePicker();
                        });
                        break;

                    default:
                        var unbindNgModel = scope.$watch('ngModel', function (newVal) {
                            scope.isActive = checkIfActive();
                            if (rulesValidationEvent == 'change') {
                                scope.validateRules();
                            }
                        });
                        break;
                }

                if (scope.datanumeric == 'true') {
                    scope.spinner = typeof attrs.spinner == 'undefined' ? 'true' : scope.spinner;
                    scope.step = typeof attrs.step == 'undefined' ? 1 : scope.step;
                    $timeout(function () {
                        angular.element('#' + scope.textfieldId).inputNumber(scope.spinner, scope.step);
                    });
                }


                function destroyEverythingElse() {
                    element.off(); // deregister all event handlers
                    $('#' + scope.textfieldId).off();
                    $('#' + scope.labelForTextfieldId).off();

                    if (unbindValidate)
                        unbindValidate();
                    if (unbindFocus)
                        unbindFocus();
                    if (unbindOptions)
                        unbindOptions();
                    if (unbindNumber)
                        unbindNumber();
                    if (unbindDateModel)
                        unbindDateModel();
                    if (angular.isFunction(scope.onDestroy)) {
                        scope.onDestroy({});
                    }
                    // Removing contents of HTML from DOM
                    $('#' + scope.textfieldId).remove();
                    $('#' + scope.labelForTextfieldId).remove();
                }
            },
            templateUrl: 'shared/directives/uiElements/smartTextfield/smartTextfieldTemplate.html'
        };
    }


    function smartTextField2Func($rootScope, $timeout, $translate, ScrollTo, RuleEngine, smartTextFieldService, smartTextField3Service, $filter) {
        return {
            restrict: 'E',
            replace: true,
            require: '?ngModel',
            scope: {
                label: '@',
                ngModel: '=?',
                options: '=?',
                isMandatory: '@',
                isVisible: '=?',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                onChange: '&',
                onFocus: '&',
                onBlur: '&',
                onKeyUp: '&',
                onKeyDown: '&',
                onKeyPress: '&',
                onEnter: '&',
                onSelect: '&',
                onDestroy: '&',
                onDateChange: '&',
                errorMessage: '@',
                errorModel: '=?',
                setFromGrid: '@',
                //  input element's attribute except for listeners should be in small letters
                type: '@',
                align: '@',
                datanumeric: '@',
                spinner: '@',
                step: '@',
                format: '@',    //  date format
                timePicker: '@', //boolean value if date with time picker
                isValueDateObj: '@',
                readonly: '@',
                disable: '@',
                prefixicon: '@',
                suffixicon: '@',
                maxlength: '@',
                min: '@',
                max: '@',
                timePicker12Hour: '@',
                minuteIncrement: '@',
                charactercounter: '@',
                decimalprecision: '@',
                minmaxprecision: '@',
                placeholder: '@',
                autocomplete: '@',
                removable: '@',
                datakey: '@',
                filterkeys: '@',
                optionformat: '@',
                displayformat: '@',
                width: '@',      //  autosuggest container width
                minchars: '@',   //  autosuggest min chars
                loading: '@',     //  loader while autosuggest data is being fetched
                count: '@',
                noSuggestionNotice: '@',
                showNoSuggestion: '@',
                clearListOnFocus: '@',
                retainDisplayModelAutocomplete: '@',
                showinfo: '@',
                showwarning: '@',
                isTitleVisible: '@',
                clienterror: '=?'
            },
            compile: function () {
                return {
                    post: function (scope, element, attrs, ngModelCtrl) {

                        if (scope.type === undefined || scope.type === 'text' || scope.type === 'area' || scope.type === 'password') {
                            smartTextField_TextFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate);
                        }
                        else if (scope.type === 'date') {

                            SmartTextField_DateFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate);
                        }
                        else if (scope.type === 'autocomplete') {

                            SmartTextField_AutocompleteFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate);

                        }
                        else if (scope.type == 'number') {
                            smartTextField_NumFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $filter, $translate);
                        }
                        else {
                            function SmartTextField_NumberFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate) {
                                scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
                                scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
                                scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
                                scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
                                scope.count = 0;
                                var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
                                var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
                                showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
                                var number, rulesValidationEvent;
                                scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
                                textfieldId++;
                                scope.textfieldId = 'tf_' + textfieldId;
                                scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

                                if (attrs.validateOn) {
                                    rulesValidationEvent = attrs.validateOn;
                                }
                                else {
                                    rulesValidationEvent = 'blur';
                                }



                                scope.onTextChange = function (e) {
                                    scope.ngModel = e.target.value;
                                    smartTextFieldService.updateTextValue(scope, element);
                                    smartTextFieldService.setActiveClass(scope, element);
                                    // if (rulesValidationEvent == 'change') {
                                    smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
                                    smartTextFieldService.updateTextFieldClass(scope, element);
                                    //  }
                                    scope.onChange({ $event: e });
                                };

                                scope.ngBlur = function (e) {

                                    scope.focus = false;
                                    smartTextFieldService.updateTextValue(scope, element);
                                    smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
                                    smartTextFieldService.setActiveClass(scope, element);
                                    smartTextFieldService.updateTextFieldClass(scope, element);




                                    scope.onBlur({ $event: e });
                                }

                                scope.ngKeyPress = function (e) {
                                    scope.onKeyPress({ $event: e });
                                };





                                /*
                             *  Following code has been added to make ng-model-options work
                             *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
                             */
                                //try {
                                //    ngModelCtrl.$render = function () {
                                //        scope.ngModel = ngModelCtrl.$modelValue;
                                //        smartTextFieldService.updateTextValue(scope, element);
                                //    };
                                //}
                                //catch (e) { }

                                scope.prevModel = scope.ngModel;
                                var updateView = function () {
                                    try {
                                        if (scope.ngModel !== scope.prevModel) {
                                            $timeout(function () {
                                                scope.prevModel = scope.ngModel;
                                            });

                                        }
                                        //ngModelCtrl.$setViewValue(scope.ngModel);
                                        smartTextFieldService.updateTextValue(scope, element);
                                    }
                                    catch (e) { }
                                };



                                var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

                                    scope.isActive = smartTextFieldService.checkIfActive(scope, element);


                                    smartTextFieldService.updateTextValue(scope, element);
                                    smartTextFieldService.setActiveClass(scope, element);
                                    smartTextFieldService.updateTextFieldClass(scope, element);
                                });

                                var unbindAttributesWatcher = scope.$watchGroup(['isMandatory', 'disable', 'readonly', 'focus', 'validate', 'isRemovable'], function (newVal, oldVal) {

                                    if (newVal[4] != oldVal[4]) {
                                        smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
                                    }
                                    smartTextFieldService.setActiveClass(scope, element);
                                    smartTextFieldService.updateTextFieldClass(scope, element);


                                });

                                scope.hideShowField = function () {
                                    scope.isVisible = !scope.isVisible;
                                };



                                smartTextFieldService.buildTemplate(scope, element, scope.onTextChange);

                                scope.$on('$destroy', function () {
                                    unbindModelWatcher();
                                    unbindAttributesWatcher();
                                });
                            }
                            SmartTextField_NumberFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate);
                        }
                        //$timeout(function () {
                        //    element.find("input").trigger('blur'); // initiate with initial value
                        //});

                    }
                }

            }
        }
    }

    function smartTextField3Func($rootScope, $timeout, $translate, ScrollTo, RuleEngine, smartTextFieldService, smartTextField3Service, $filter, $window) {
        return {
            restrict: 'E',
            replace: true,
            require: '?ngModel',
            scope: {
                label: '@',
                ngModel: '=?',
                options: '=?',
                isMandatory: '@',
                isVisible: '=?',
                rules: '@',
                validate: '=?',
                focus: '=?',
                parentElement: '@',
                onChange: '&',
                onFocus: '&',
                onBlur: '&',
                onKeyUp: '&',
                onKeyDown: '&',
                onSearch: '&',
                onKeyPress: '&',
                onEnter: '&',
                onSelect: '&',
                onDestroy: '&',
                onDateChange: '&',
                errorMessage: '@',
                errorModel: '=?',
                setFromGrid: '@',
                //  input element's attribute except for listeners should be in small letters
                type: '@',
                align: '@',
                datanumeric: '@',
                spinner: '@',
                step: '@',
                format: '@',    //  date format
                regionalFormat: '@',    //  regional date format
                timePicker: '@', //boolean value if date with time picker
                isValueDateObj: '@',
                readonly: '@',
                disable: '@',
                prefixicon: '@',
                suffixicon: '@',
                maxlength: '@',
                min: '@',
                max: '@',
                timePicker12Hour: '@',
                minuteIncrement: '@',
                charactercounter: '@',
                decimalprecision: '@',
                minmaxprecision: '@',
                placeholder: '@',
                autocomplete: '@',
                removable: '@',
                datakey: '@',
                filterkeys: '@',
                optionformat: '@',
                displayformat: '@',
                width: '@',      //  autosuggest container width
                minchars: '@',   //  autosuggest min chars
                loading: '@',     //  loader while autosuggest data is being fetched
                count: '@',
                noSuggestionNotice: '@',
                showNoSuggestion: '@',
                clearListOnFocus: '@',
                retainDisplayModelAutocomplete: '@',
                showinfo: '@',
                showwarning: '@',
                isTitleVisible: '@',
                clienterror: '=?',
                infinite: '@'
            },
            compile: function () {
                return {
                    post: function (scope, element, attrs, ngModelCtrl) {
                        if (scope.type === undefined || scope.type === 'text' || scope.type === 'area' || scope.type === 'password') {
                            smartTextField3_TextFunc(scope, element, attrs, ngModelCtrl, smartTextField3Service, $timeout, $translate);
                        }
                        else if (scope.type === 'date') {

                            SmartTextField3_DateFunc(scope, element, attrs, ngModelCtrl, smartTextField3Service, $timeout, $translate);
                        }
                        else if (scope.type === 'autocomplete') {

                            SmartTextField3_AutocompleteFunc(scope, element, attrs, ngModelCtrl, smartTextField3Service, $timeout, $translate, $window);

                        }
                        else if (scope.type == 'number') {
                            smartTextField3_NumFunc(scope, element, attrs, ngModelCtrl, smartTextField3Service, $timeout, $filter, $translate);
                        }
                        else {
                            function SmartTextField_NumberFunc(scope, element, attrs, ngModelCtrl, smartTextField3Service, $timeout, $translate) {
                                scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
                                scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
                                scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
                                scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
                                scope.count = 0;
                                var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
                                var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
                                showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
                                var number, rulesValidationEvent;
                                scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
                                textfieldId++;
                                scope.textfieldId = 'tf_' + textfieldId;
                                scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

                                if (attrs.validateOn) {
                                    rulesValidationEvent = attrs.validateOn;
                                }
                                else {
                                    rulesValidationEvent = 'blur';
                                }



                                scope.onTextChange = function (e) {
                                    scope.ngModel = e.target.value;
                                    smartTextField3Service.updateTextValue(scope, element);
                                    smartTextField3Service.setActiveClass(scope, element);
                                    // if (rulesValidationEvent == 'change') {
                                    smartTextField3Service.validateRules(scope, ngModelCtrl, element, attrs);
                                    smartTextField3Service.updateTextFieldClass(scope, element);
                                    //  }
                                    scope.onChange({ $event: e });
                                };

                                scope.ngBlur = function (e) {

                                    scope.focus = false;
                                    smartTextField3Service.updateTextValue(scope, element);
                                    smartTextField3Service.validateRules(scope, ngModelCtrl, element, attrs);
                                    smartTextField3Service.setActiveClass(scope, element);
                                    smartTextField3Service.updateTextFieldClass(scope, element);




                                    scope.onBlur({ $event: e });
                                }

                                scope.ngKeyPress = function (e) {
                                    scope.onKeyPress({ $event: e });
                                };





                                /*
                             *  Following code has been added to make ng-model-options work
                             *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
                             */
                                //try {
                                //    ngModelCtrl.$render = function () {
                                //        scope.ngModel = ngModelCtrl.$modelValue;
                                //        smartTextField3Service.updateTextValue(scope, element);
                                //    };
                                //}
                                //catch (e) { }

                                scope.prevModel = scope.ngModel;
                                var updateView = function () {
                                    try {
                                        if (scope.ngModel !== scope.prevModel) {
                                            $timeout(function () {
                                                scope.prevModel = scope.ngModel;
                                            });

                                        }
                                        //ngModelCtrl.$setViewValue(scope.ngModel);
                                        smartTextField3Service.updateTextValue(scope, element);
                                    }
                                    catch (e) { }
                                };



                                var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

                                    scope.isActive = smartTextField3Service.checkIfActive(scope, element);


                                    smartTextField3Service.updateTextValue(scope, element);
                                    smartTextField3Service.setActiveClass(scope, element);
                                    smartTextField3Service.updateTextFieldClass(scope, element);
                                });

                                var unbindAttributesWatcher = scope.$watchGroup(['isMandatory', 'disable', 'readonly', 'focus', 'validate', 'isRemovable'], function (newVal, oldVal) {

                                    if (newVal[4] != oldVal[4]) {
                                        smartTextField3Service.validateRules(scope, ngModelCtrl, element, attrs);
                                    }
                                    smartTextField3Service.setActiveClass(scope, element);
                                    smartTextField3Service.updateTextFieldClass(scope, element);


                                });

                                scope.hideShowField = function () {
                                    scope.isVisible = !scope.isVisible;
                                };



                                smartTextField3Service.buildTemplate(scope, element, scope.onTextChange);

                                scope.$on('$destroy', function () {
                                    unbindModelWatcher();
                                    unbindAttributesWatcher();
                                });
                            }
                            SmartTextField_NumberFunc(scope, element, attrs, ngModelCtrl, smartTextField3Service, $timeout, $translate);
                        }
                        //$timeout(function () {
                        //    element.find("input").trigger('blur'); // initiate with initial value
                        //});

                    }
                }

            }
        }
    }

    function smartTextField_TextFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate) {
        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
        var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
        showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);
        scope.showTitle = scope.isTitleVisible == undefined || scope.isTitleVisible == null || scope.isTitleVisible == 'true' ? true : false;

        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }

        scope.onTextChange = function (e) {
            //scope.ngModel = e.target.value;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);

            smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
            smartTextFieldService.setActiveClass(scope, element);
            // if (rulesValidationEvent == 'change') {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.updateTextFieldClass(scope, element);
            //  }
            scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {
            scope.focus = false;
            scope.onBlur({ $event: e });
            $timeout(function () {
                //updateView();

                smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
            });
        }

        scope.ngKeyPress = function (e) {
            scope.typing = true;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);
            updateView();
            if (attrs.onKeyPress) {
                scope.onKeyPress({ $event: e });
            }
            if (attrs.onEnter && e.charCode == 13) {
                scope.onEnter({ $event: e });
            }
        };

        scope.ngKeyUp = function (e) {
            scope.typing = true;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);
            updateView();
            scope.onKeyUp({ $event: e });
        };

        scope.ngKeyDown = function (e) {
             scope.typing = true;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);
            updateView();
            scope.onKeyDown({ $event: e });
        };

        scope.ngFocus = function (e) {
            scope.onFocus({ $event: e });
        };

        scope.prevModel = smartTextFieldService.modal.getModal(scope, ngModelCtrl);
        var updateView = function () {
            try {
                if (smartTextFieldService.modal.getModal(scope, ngModelCtrl) !== scope.prevModel) {

                    /* Removed $timeout due to ngBlur gets fired before scope.ngModel */
                    $timeout(function () {
                        scope.prevModel = smartTextFieldService.modal.getModal(scope, ngModelCtrl);

                        if (typeof scope.ngModel === 'function') {
                            smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                        }
                    });
                    //scope.$apply();
                }
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
            }
            catch (e) { }
        };

        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateTextValue(scope, element);
        //    };
        //}
        //catch (e) { }
        var unbindModelWatcher;
        if (typeof scope.ngModel != 'function') {
            unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

                scope.isActive = smartTextFieldService.checkIfActive(scope, element);
                if(scope.typing === true) scope.typing = false;
                smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
            });
        } else {
            unbindModelWatcher = scope.$watch(function () { return scope.ngModel(); }, function (newVal, oldVal) {
                if (newVal != oldVal) {
                     scope.typing = false;
                    scope.isActive = smartTextFieldService.checkIfActive(scope, element);


                    smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                    smartTextFieldService.setActiveClass(scope, element);
                    smartTextFieldService.updateTextFieldClass(scope, element);
                }

            }, true);
        }

        //var unbindAttributesWatcher = scope.$watchGroup(['isMandatory', 'disable', 'readonly', 'focus', 'validate', 'isRemovable'], function (newVal, oldVal) {

        //    if (newVal[4] != oldVal[4]) {
        //        smartTextFieldService.validateRules(scope, ngModelCtrl, element,attrs);
        //    }
        //    smartTextFieldService.setActiveClass(scope, element);
        //    smartTextFieldService.updateTextFieldClass(scope, element);


        //});

        var unbindOtherWatcher;
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();
            }

        });
        unbindOtherWatcher = scope.$watchGroup(['isMandatory', 'focus', 'validate', 'isRemovable', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
            if (newVal[2] != oldVal[2] || newVal[4] != oldVal[4]) { //validate
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);

                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
            }
            if (newVal[5] != oldVal[5] || newVal[6] != oldVal[6]) {
                if (scope.showinfo || scope.showwarning) {
                    element.find('> .input-field').addClass('field-has-icon');
                }
                else {
                    element.find('> .input-field').removeClass('field-has-icon');
                }
            }
            if (newVal[1] || newVal[1] == "true") {
                element.find('input').focus();
            }
        });

        //function bindOtherWatcher() {
        //    if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
        //        //if (!angular.isDefined(unbindOtherWatcher)) {

        //        //}
        //    }
        //    else {
        //        if (angular.isDefined(unbindOtherWatcher)) {
        //            unbindOtherWatcher();
        //        }
        //    }
        //}
        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };
        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange, attrs);
        updateView();

        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }

        //bindOtherWatcher();
        //setTimeout(function () {
        //    //if (scope.readonly == 'true') {
        //    //    angular.element('#' + scope.textfieldId).attr('tabindex', -1);
        //    //}
        //    try {
        //        if (angular.isDefined(scope.$parent.$parent.onEditableRender)) {
        //            scope.$parent.$parent.onEditableRender(scope, element);
        //        }
        //    }
        //    catch (e) { }
        //});

        scope.$on('$destroy', function () {
            unbindModelWatcher();
            unbindAttributesWatcher();
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
        });
    }

    function SmartTextField_DateFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate) {
        if (scope.timePicker === 'true') {
            scope.timePicker = true;
        }

        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null || scope.placeholder == "" ? moment.localeData()._longDateFormat.L : scope.placeholder;
        if (scope.timePicker == true) {
            scope.placeholder = moment.localeData()._longDateFormat.L + ' ' + moment.localeData()._longDateFormat.LT;
        }

        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        scope.datetimePicker = (typeof scope.timePicker == undefined ? false : scope.timePicker === true ? true : false);
        scope.format = (scope.format ? scope.format : scope.datetimePicker ? 'DD/MM/YYYY h:mm' : 'DD/MM/YYYY');
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

        scope.applyClientZone = false;
        scope.isValueDateObj = false;
        scope.doesContainDateString = false;
        scope.dateFormat = scope.format;
        scope.isDateBlank = false;

        if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
            scope.dateFormat = moment.localeData().longDateFormat('L');
            if (scope.timePicker == true) {
                scope.dateFormat = moment.localeData().longDateFormat('L') + ' ' + moment.localeData().longDateFormat('LT');
            }
            scope.format = scope.dateFormat;
        }
        scope.convertIntoUTC = false;
        if (typeof convertIntoUTC != 'undefined') {
            scope.convertIntoUTC = convertIntoUTC;
        }

        //if (angular.isDefined(scope.$parent.$parent.property)) {
        //    //this property (applyClientZone) comes from Config.js
        //    if (angular.isDefined(scope.$parent.$parent.property.applyClientZone)) {
        //        scope.applyClientZone = scope.$parent.$parent.property.applyClientZone;
        //    }
        //}



        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }



        scope.onTextChange = function (e) {
            //scope.ngModel = e.target.value;
            // smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.setActiveClass(scope, element);
            // if (rulesValidationEvent == 'change') {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.updateTextFieldClass(scope, element);
            //  }
            scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {
            if (scope.isDateBlank) {
                var targetedEle = document.activeElement;
                if (!(angular.element(targetedEle).hasClass('monthselect') || angular.element(targetedEle).hasClass('yearselect') || angular.element(targetedEle).hasClass('hourselect') || angular.element(targetedEle).hasClass('minuteselect'))) {
                    //  scope.ngModel = null;
                    scope.dateModel = null;
                }
                if (angular.isFunction(scope.onDateChange)) {
                    scope.onDateChange({
                        $date: scope.ngModel
                    });
                }
            }

            scope.focus = false;
            //smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);




            scope.onBlur({ $event: e });
        }

        scope.ngKeyUp = function (e) {
            if (e.which != 32) {
                var currentInputValue = $('#' + scope.textfieldId).val();
                if (currentInputValue == "") {
                    scope.ngModel = null;
                    scope.dateModel = ""
                    scope.isDateBlank = true;
                    if (angular.isFunction(scope.onDateChange)) {
                        scope.onDateChange({
                            $date: scope.ngModel
                        });
                    }
                }
            }

            if (rulesValidationEvent == 'keyUp') {
                scope.validateRules();
            }
            scope.onKeyUp({ $event: e });
        };

        scope.ngKeyDown = function (e) {
            if (rulesValidationEvent == 'keyDown') {
                scope.validateRules();
            }
            ////to control number of down arrow,up arrow signs on number fields
            //if (e.keyCode == 38 || e.keyCode == 40)
            //    e.preventDefault();
            scope.onKeyDown({ $event: e });
        };

        scope.ngKeyPress = function (e) {

            if (rulesValidationEvent == 'keyPress') {
                scope.validateRules();
            }
            scope.onKeyPress({ $event: e });
            if (e.charCode == 13) {
                scope.onEnter({ $event: e });
            }
        };





        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateDateValue(scope, element);
        //    };
        //}
        //catch (e) { }

        scope.prevModel = scope.ngModel;
        var updateView = function () {
            try {
                if (scope.ngModel !== scope.prevModel) {
                    $timeout(function () {
                        scope.prevModel = scope.ngModel;
                    });

                }
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateDateValue(scope, element);
            }
            catch (e) { }
        };



        //var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {


        //});


        var unbindOtherWatcher;
        var unbindMin;
        var unbindMax
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();
            }

        });

        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };

        function bindOtherWatcher() {
            if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
                if (!angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher = scope.$watchGroup(['isMandatory', 'focus', 'validate', 'isRemovable', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
                        if (newVal[2] != oldVal[2] || newVal[4] != oldVal[4]) { //validate
                            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);

                            smartTextFieldService.setActiveClass(scope, element);
                            smartTextFieldService.updateTextFieldClass(scope, element);
                        }

                        if (newVal[5] != oldVal[5] || newVal[6] != oldVal[6]) {
                            if (scope.showinfo || scope.showwarning) {
                                element.find('> .input-field').addClass('field-has-icon');
                            }
                            else {
                                element.find('> .input-field').removeClass('field-has-icon');
                            }
                        }
                    });
                }
                if (!angular.isDefined(unbindMin)) {
                    unbindMin = scope.$watch('min', function (n) {
                        smartTextFieldService.createDatePicker(scope, element, ngModelCtrl);
                    });
                }
                if (!angular.isDefined(unbindMax)) {
                    unbindMax = scope.$watch('max', function (n) {
                        smartTextFieldService.createDatePicker(scope, element, ngModelCtrl);
                    });
                }

            }
            else {
                if (angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher();
                }
                if (angular.isDefined(unbindMin)) {
                    unbindMin();
                }
                if (angular.isDefined(unbindMax)) {
                    unbindMax();
                }
            }
        }




        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange);
        smartTextFieldService.updateDateClass(scope, element);
        smartTextFieldService.assignDateEvents(scope, element);
        bindOtherWatcher();
        var unbindDateModel = scope.$watch('dateModel', function (newVal) {
            if (newVal) {
                scope.isActive = smartTextFieldService.checkIfActive(scope, element);
                if (rulesValidationEvent == 'change') {
                    scope.validateRules();
                }
                smartTextFieldService.updateDateValue(scope, element);
            }
        });
        var timeOffset = 20; // this is a fixed number in-case of timeZoneOffset is not defined.
        scope.updatingDateModel = function (dateObj) {
            if (typeof timeZoneOffset != 'undefined') {
                timeOffset = timeZoneOffset;
            }

            if (timeOffset == 20) {
                var adjustTimeZone = 0;
                if (moment().zone() == -720) {
                    adjustTimeZone = -1;
                }
                scope.dateModel = moment(dateObj).add(adjustTimeZone, 'second').format(scope.dateFormat);
            }
            else {
                var adjustTimeZone = 0;
                if (timeOffset == -12) {
                    adjustTimeZone = -1;
                }

                if (typeof dateObj == 'object') {
                    if (timeZoneOffset != 20) {
                        scope.dateModel = moment(dateObj).add(timeOffset * 60, 'minute').add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                    else {
                        scope.dateModel = moment(dateObj).add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                } else {
                    if (timeZoneOffset != 20) {
                        scope.dateModel = moment(dateObj).utc().zone(timeOffset * -1).add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                    else {
                        scope.dateModel = moment(dateObj).utc().add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                    //scope.dateModel = moment(dateObj).zone(timeOffset * -1).utc().add(adjustTimeZone, 'second').format(scope.dateFormat);
                }
            }

        }
        // should be modified to discard UTC Convertion 
        var unbindNgModel = scope.$watch('ngModel', function (newVal) {
            if (scope.timePicker === 'true') {
                scope.timePicker = true;
            }
            if (!scope.dateModifiedbyUser) {
                scope.dateFormat = scope.format.replace(/d/g, 'D').replace(/y/g, 'Y').replace(/E/g, 'd');
                if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
                    scope.dateFormat = moment.localeData().longDateFormat('L');
                    if (scope.timePicker == true) {
                        scope.dateFormat = moment.localeData().longDateFormat('L') + ' ' + moment.localeData().longDateFormat('LT');
                    }
                    scope.format = scope.dateFormat;
                }

                scope.isDateBlank = false;
                scope.doesContainDateString = scope.setFromGrid == 'true' ? true : (newVal + '').toLowerCase().indexOf('date') > -1 ? true : false;
                if (newVal == "" || newVal == null || typeof newVal == 'function') {
                    newVal = Date.now();
                    scope.isDateBlank = true;
                    scope.isValueDateObj = false;
                } else {
                    if (scope.doesContainDateString) {
                        var dateStr = (newVal + '').replace(new RegExp("/", "ig"), '');
                        var tmpModel = dateStr;
                        if (dateStr.indexOf('Date') <= -1) {
                            tmpModel = tmpModel.split('+');
                            tmpModel = eval('new ' + (tmpModel.length > 1 ? ('Date("' + newVal + '").getTime()') : tmpModel[0]));
                        }
                        scope.updatingDateModel(tmpModel);
                        //scope.dateModel = moment(tmpModel).format(scope.dateFormat);
                        //smartTextFieldService.updateDateValue(scope, element);
                    }
                    else {
                        if (Object.prototype.toString.call(newVal) === '[object Date]') {
                            scope.isValueDateObj = true;
                        } else if (typeof newVal === 'string' && newVal.indexOf('T') > 0) {
                            
                        } else {
                            newVal = parseInt(newVal);
                        }
                    }
                }

                scope.placeholder = scope.format;
                //scope.placeholder = scope.placeholder == undefined || scope.placeholder == null || scope.placeholder == "" ? 'DD/MM/YYYY' : scope.placeholder; // to consider custom placeholder value and not only the format of the date-time picker

                if (!scope.isDateBlank) {
                    if (!scope.doesContainDateString) {
                        //if (scope.applyClientZone) {
                        //    scope.dateModel = moment(newVal).parseZone().format(scope.dateFormat);
                        //}
                        //else {
                        scope.updatingDateModel(newVal);
                        //scope.dateModel = moment(newVal).format(scope.dateFormat);
                        //}
                    }
                } else {
                    scope.dateModel = "";
                }
                smartTextFieldService.updateDateValue(scope, element);
                smartTextFieldService.createDatePicker(scope, element, ngModelCtrl);
            }
            scope.isActive = smartTextFieldService.checkIfActive(scope, element);
            scope.dateModifiedbyUser = false;

            //smartTextFieldService.updateDateValue(scope, element);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);

        });

        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }

        scope.$on('$destroy', function () {
            //unbindModelWatcher();
            unbindAttributesWatcher();
            unbindNgModel();
            unbindDateModel();
            if (angular.isDefined(unbindMin)) {
                unbindMin();
            }
            if (angular.isDefined(unbindMax)) {
                unbindMax();
            }
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
        });
    }

    function SmartTextField_AutocompleteFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate) {
        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
        var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
        showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }



        scope.onTextChange = function (e) {

            console.log(scope.displayModel);

            //updateView();

            //console.log(scope.displayModel);
            scope.onChange({
                $event: {
                    data: element.find('#' + scope.textfieldId),
                    displayModel: scope.displayModel
                }
            });

            ////scope.ngModel = e.target.value;
            //smartTextFieldService.updateAutocompleteValue (scope, element);
            //smartTextFieldService.setActiveClass(scope, element);
            //// if (rulesValidationEvent == 'change') {
            //smartTextFieldService.validateRules(scope, ngModelCtrl, element);
            //smartTextFieldService.updateTextFieldClass(scope, element);
            ////  }
            ////scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {


            if (scope.ngModel && scope.displayformat) {
                var splittedDisplayFormat = [];
                if (scope.displayformat.indexOf(":") > 0)
                    splittedDisplayFormat = scope.displayformat.split(':');
                else if (scope.displayformat.indexOf("-") > 0)
                    splittedDisplayFormat = scope.displayformat.split('-');
                else
                    splittedDisplayFormat = scope.displayformat.split('-');
                var result = "";
                for (var i = 0; i < splittedDisplayFormat.length; i++) {
                    result += eval("scope.ngModel." + splittedDisplayFormat[i].replace('{', '').replace('}', ''));
                    if (i < splittedDisplayFormat.length - 1) {
                        if (scope.displayformat.indexOf("-") > 0)
                            result += '-';
                        else if (scope.displayformat.indexOf(":") > 0)
                            result += ':';
                    }
                }

                // P2P-16866
                if (scope.retainDisplayModelAutocomplete !== "true") {

                    if (e.target.value.trim() == '' || result !== scope.displayModel) {
                        scope.displayModel = '';
                    }
                }

            }


            scope.focus = false;
            smartTextFieldService.updateAutocompleteValue(scope, element);
            $timeout(function () {
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            });
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);

            try {
                if (angular.isDefined(scope.$parent.$parent.onStopEdit)) {
                    scope.$parent.$parent.onStopEdit(scope, element);
                }
            }
            catch (e) { }



            scope.onBlur({ $event: e });
        }

        scope.ngKeyPress = function (e) {
            ////console.log("The text has been changed.");
            ////updateView();

            ////var val = e.target.value;

            //scope.displayModel = e.target.value;

            if (rulesValidationEvent == 'keyPress') {
                scope.validateRules();
            }
            scope.onKeyPress({ $event: e });
            if (e.charCode == 13) {
                scope.onEnter({ $event: e });
            }


        };
        scope.elmVal = null;
        scope.ngKeyUp = function (e) {
            ////console.log("The text has been changed.");
            ////updateView();

            //if (e.target.value != '' && e.target.value != scope.elmVal) {
            //    scope.elmVal = e.target.value;
            //    scope.onChange({
            //        $event: {
            //            data: element.find('#' + scope.textfieldId),
            //            displayModel: e.target.value
            //        }
            //    });
            //}




        };

        scope.search = function (e) {
            if (e.target.value != '' && e.target.value != scope.elmVal) {
                scope.elmVal = e.target.value;
                scope.onChange({
                    $event: {
                        data: element.find('#' + scope.textfieldId),
                        displayModel: e.target.value
                    }
                });
            }
        };

        scope.ngFocus = function (e) {
            var elm = element.find('#' + scope.textfieldId);
            var Obj = elm.devbridgeAutocomplete();
            if (elm.val() == '' && Obj) {
                Obj.clear();
                scope.elmVal = '';
            }
            scope.onFocus({ $event: e });
        }


        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateAutocompleteValue(scope, element);
        //    };
        //}
        //catch (e) { }

        scope.prevModel = scope.ngModel;
        var updateView = function () {
            try {
                if (scope.ngModel !== scope.prevModel) {
                    $timeout(function () {
                        scope.prevModel = scope.ngModel;
                    });

                }
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateAutocompleteValue(scope, element);
            }
            catch (e) { }
        };



        var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

            scope.displayModel = smartTextFieldService.getDisplayData(newVal, scope);

            scope.isActive = smartTextFieldService.checkIfActive(scope, element);


            smartTextFieldService.updateAutocompleteValue(scope, element);
            smartTextFieldService.validateRules(scope, ngModelCtrl, element,attrs);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);
        });

        var unbindOtherWatcher;
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();

            }

        });

        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };

        function bindOtherWatcher() {
            if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
                if (!angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher = scope.$watchGroup(['focus', 'isRemovable'], function (newVal, oldVal) {
                        smartTextFieldService.setActiveClass(scope, element);
                        smartTextFieldService.updateTextFieldClass(scope, element);
                    });
                }
            }
            else {
                if (angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher();
                }
            }
        }


        var unbindValidationWatcher = scope.$watchGroup(['isMandatory', 'validate', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
            if (newVal[1] != oldVal[1] || newVal[2] != oldVal[2]) {
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            }
            if (newVal[3] != oldVal[3] || newVal[4] != oldVal[4]) {
                if (scope.showinfo || scope.showwarning) {
                    element.find('> .input-field').addClass('field-has-icon');
                }
                else {
                    element.find('> .input-field').removeClass('field-has-icon');
                }
            }
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);
        });







        // key to be used to bind options
        var filterKeys;
        if (angular.isDefined(scope.filterkeys) && scope.filterkeys.trim().indexOf('[') == 0) {
            filterKeys = scope.filterkeys ? scope.$eval(scope.filterkeys) : ['title'];
        }
        else {
            filterKeys = scope.filterkeys ? scope.$eval('[' + scope.filterkeys + ']') : ['title'];
        }
        var optionFormat = scope.optionformat ? scope.optionformat : '{title}';
        scope.ndisplayFormat = scope.displayformat ? scope.displayformat : '{title}';

        /*
         * Check if the options are updated
         */
        var unbindOptions = scope.$watch('options', function (newOptions) {
            try {
                if (!$('#' + scope.textfieldId).devbridgeAutocomplete()) {
                    $('#' + scope.textfieldId).devbridgeAutocomplete({
                        filterKeys: filterKeys,
                        optionFormat: optionFormat,
                        displayFormat: scope.ndisplayFormat,
                        lookup: smartTextFieldService.getFormattedData(newOptions, scope),
                        width: scope.width,
                        showNoSuggestionNotice: showNoSuggestion,
                        noSuggestionNotice: noSuggestionNotice,
                        minChars: angular.isDefined(scope.minchars) ? parseInt(scope.minchars) : 1,
                        onSelect: function (suggestion) {
                            angular.element(document).trigger('click');
                            scope.$evalAsync(function () {
                                scope.displayModel = smartTextFieldService.getDisplayData(suggestion, scope);
                                scope.ngModel = suggestion;
                                updateView();
                                $timeout(function () {
                                    scope.onSelect({
                                        $event: {
                                            data: suggestion
                                        }
                                    });
                                });
                            });
                        }
                    });
                }
                else {
                    $('#' + scope.textfieldId).devbridgeAutocomplete().setLookup(smartTextFieldService.getFormattedData(newOptions, scope));
                }
            }
            catch (e) { }
        });



        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange, attrs);
        smartTextFieldService.assignAutocompleteEvents(scope, element);
        bindOtherWatcher();

        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }

        scope.$on('$destroy', function () {
            unbindModelWatcher();
            unbindAttributesWatcher();
            unbindOptions();
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
            unbindValidationWatcher();
        });
    }

    function smartTextField_NumFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $filter, $translate) {
        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
        var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
        showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);
        scope.showTitle = scope.isTitleVisible == undefined || scope.isTitleVisible == null || scope.isTitleVisible == 'true' ? true : false;


        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }



        scope.onTextChange = function (e) {

            // if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
            var num = element.find('#' + scope.textfieldId).autoNumeric('get');
            if (num != '') {
                scope.ngModel = parseFloat(element.find('#' + scope.textfieldId).autoNumeric('get'));
            }
            else {
                scope.ngModel = null;
            }
            //}
            //else {
            //    scope.ngModel = parseFloat(e.target.value);
            //}
            updateView();
            //scope.ngModel = parseFloat(e.target.value);
            //smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.setActiveClass(scope, element);
            // if (rulesValidationEvent == 'change') {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.updateTextFieldClass(scope, element);
            //  }
            scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {
            //if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {

            var num = element.find('#' + scope.textfieldId).autoNumeric('get');
            if (num != '') {
                scope.ngModel = parseFloat(element.find('#' + scope.textfieldId).autoNumeric('get'));
            }
            else {
                scope.ngModel = null;
            }
            //}
            //else {
            //    var num = e.target.value;
            //    if (num.trim() != '') {
            //        scope.ngModel = parseFloat(num);
            //    }
            //    else {
            //        scope.ngModel = null;
            //    }
            //}
            updateView();
            scope.focus = false;
            //smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);




            scope.onBlur({ $event: e });
        }

        scope.ngKeyPress = function (e) {
            if (scope.type == 'number' || attrs.datanumeric) {
                //to control number of +,-, signs and alphabets on number fields
                var charCode = e.charCode || e.keyCode,
                textValue = e.currentTarget.value,
                decimalDigit = [],
                cc = String.fromCharCode(charCode),
                escapeChars = [37, 38, 39, 40, 46, 8];

                if (isNaN(cc) && (escapeChars.indexOf(charCode) == -1)) {
                    e.preventDefault();
                }
                // Restrict the 2 dots entering
                if (textValue.toString().indexOf('.') !== -1 && charCode == 46) {
                    e.preventDefault();
                }

                if (textValue) {
                    decimalDigit = textValue.toString().split('.');
                }
                // Restrict the decimal value from entering
                if (attrs.decimalprecision) {
                    if (charCode == 46 && parseInt(attrs.decimalprecision) == 0) {
                        e.preventDefault();
                    }
                    if (decimalDigit.length == 2) {
                        if (decimalDigit[1] && decimalDigit[1].length >= parseInt(attrs.decimalprecision, 10)) {
                            e.preventDefault();
                        }
                    }
                }
                if (scope.min != undefined || scope.min != null) {
                    if (scope.min >= 0 && e.charCode == 45) {
                        e.preventDefault();
                    }
                }
            }

            if (rulesValidationEvent == 'keyPress') {
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            }
            scope.onKeyPress({ $event: e });
            if (e.charCode == 13) {
                scope.onEnter({ $event: e });
            }

            // because of debounce has applied on smart-textfield, here we need to do DOM manipulation i.e. e.target.value to get latest value from textfield
            if (angular.isDefined(attrs.maxlength) && e.target.value.length == attrs.maxlength && e.charCode !== 0) {
                e.preventDefault();
            }
        };




        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateTextValue(scope, element);
        //    };
        //}
        //catch (e) { }

        scope.prevModel = scope.ngModel;
        var updateView = function () {
            try {
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateTextValue(scope, element);
                //smartTextFieldService.updateTextValue(scope, element);
                if (scope.ngModel != null) {
                    var min = 0;
                    var max = 0;
                    if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
                        if (scope.minmaxprecision == undefined || scope.minmaxprecision == '') {
                            min = 0;
                            max = 0;
                        }
                        else {
                            min = MinPrecessionValue;//global default
                            max = MaxPrecessionValue;//global default
                        }
                    }
                    else {
                        if (scope.minmaxprecision !== undefined && scope.minmaxprecision !== '') {
                            var splitPrecision = JSON.parse(scope.minmaxprecision);
                            if (splitPrecision.length == 2) {
                                min = splitPrecision[0];
                                max = splitPrecision[1];
                            }
                        }
                    }
                    try {
                        if (scope.minmaxprecision) {
                            min = JSON.parse(scope.minmaxprecision)[0];
                            max = JSON.parse(scope.minmaxprecision)[1];
                        }
                    }
                    catch (e) {
                        console.log(e)
                    }
                    element.find('#' + scope.textfieldId).val($filter('minPrecisionHandler')(scope.ngModel, min, max, false))
                    if (!scope.ngModel) {
                        var num = element.find('#' + scope.textfieldId).autoNumeric('get');
                        if (num != '') {
                            scope.ngModel = parseFloat(element.find('#' + scope.textfieldId).autoNumeric('get'));
                            smartTextFieldService.setActiveClass(scope, element);
                            smartTextFieldService.updateTextFieldClass(scope, element);
                        }
                        else {
                            scope.ngModel = null;
                        }
                    }

                    if (scope.ngModel !== scope.prevModel) {
                        $timeout(function () {
                            scope.prevModel = scope.ngModel;
                        });
                    }


                }
            }
            catch (e) { }
        };



        var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

            scope.isActive = smartTextFieldService.checkIfActive(scope, element);


            updateView();
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);
        });


        //var unbindAttributesWatcher = scope.$watchGroup(['isMandatory', 'disable', 'readonly', 'focus', 'validate', 'isRemovable'], function (newVal, oldVal) {

        //    if (newVal[4] != oldVal[4]) {
        //        smartTextFieldService.validateRules(scope, ngModelCtrl, element,attrs);
        //    }
        //    smartTextFieldService.setActiveClass(scope, element);
        //    smartTextFieldService.updateTextFieldClass(scope, element);


        //});

        var unbindOtherWatcher;
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();
            }

        });


        function bindOtherWatcher() {
            if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
                if (!angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher = scope.$watchGroup(['isMandatory', 'focus', 'validate', 'isRemovable', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
                        if (newVal[2] != oldVal[2] || newVal[4] != oldVal[4]) { //validate
                            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);

                            smartTextFieldService.setActiveClass(scope, element);
                            smartTextFieldService.updateTextFieldClass(scope, element);
                        }
                        if (newVal[5] != oldVal[5] || newVal[6] != oldVal[6]) {
                            if (scope.showinfo || scope.showwarning) {
                                element.find('> .input-field').addClass('field-has-icon');
                            }
                            else {
                                element.find('> .input-field').removeClass('field-has-icon');
                            }
                        }
                    });
                }
            }
            else {
                if (angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher();
                }
            }
        }




        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };



        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange);
        if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
            if (scope.minmaxprecision == undefined || scope.minmaxprecision == '') {
                element.find('#' + scope.textfieldId).autoNumeric('init', {
                    mDec: 0,
                    aPad: false,
                    aSep: thousandSep,
                    aDec: decimalSep,
                    vMin: Number.NEGATIVE_INFINITY,
                    vMax: Number.POSITIVE_INFINITY
                });
            } else {
                element.find('#' + scope.textfieldId).autoNumeric();
            }
        } else {
            var thousandSep = ',';
            var decimalSep = '.';
            var highPrecisionLimit = 0;
            if (scope.minmaxprecision !== undefined && scope.minmaxprecision !== '') {
                var splitPrecision = JSON.parse(scope.minmaxprecision);
                if (splitPrecision.length == 2) {
                    var lowerPrecisionLimit = splitPrecision[0];
                    highPrecisionLimit = splitPrecision[1];
                }
            }
            element.find('#' + scope.textfieldId).autoNumeric('init', {
                mDec: highPrecisionLimit,
                aPad: false,
                aSep: thousandSep,
                aDec: decimalSep,
                vMin: Number.NEGATIVE_INFINITY,
                vMax: Number.POSITIVE_INFINITY,
            });
        }
        bindOtherWatcher();
        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }
        //setTimeout(function () {
        //    //if (scope.readonly == 'true') {
        //    //    angular.element('#' + scope.textfieldId).attr('tabindex', -1);
        //    //}
        //    try {
        //        if (angular.isDefined(scope.$parent.$parent.onEditableRender)) {
        //            scope.$parent.$parent.onEditableRender(scope, element);
        //        }
        //    }
        //    catch (e) { }
        //});

        scope.$on('$destroy', function () {
            unbindModelWatcher();
            unbindAttributesWatcher();
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
        });
    }



    function smartTextField3_TextFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate) {
        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
        var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
        showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
          //  following line of code has been added for pendo
          var tmpId = '';
          if(attrs.id){
              tmpId = "tf_" + attrs.id;        
              var tmpElementCount = $('#' + tmpId).length;
              if(tmpElementCount > 0) {
                  tmpId = tmpId + '_' + textfieldId;
              }
              element.removeAttr('id');
          }else {
              tmpId = 'tf_' + textfieldId;
          }    
          scope.textfieldId = tmpId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);
        scope.showTitle = scope.isTitleVisible == undefined || scope.isTitleVisible == null || scope.isTitleVisible == 'true' ? true : false;
        scope.typing = false;
        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }
        if (scope.type === 'area') {
            $timeout(function () {
                angular.element('#' + scope.textfieldId).trigger('autoresize');
            });
        }
        scope.onTextChange = function (e) {
            //scope.ngModel = e.target.value;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);

            smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
            smartTextFieldService.setActiveClass(scope, element, ngModelCtrl);
            // if (rulesValidationEvent == 'change') {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.updateTextFieldClass(scope, element);
            //  }
            scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {
            $timeout(function () {
                //updateView();
                scope.focus = false;
                scope.typing = false;
                smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
                smartTextFieldService.setActiveClass(scope, element, ngModelCtrl);
                smartTextFieldService.updateTextFieldClass(scope, element);

                scope.onBlur({ $event: e });
            });
        }

        scope.ngKeyPress = function (e) {
            scope.typing = true;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);
            updateView();
            if (attrs.onKeyPress) {
                scope.onKeyPress({ $event: e });
            }
            if (attrs.onEnter && e.charCode == 13) {
                scope.onEnter({ $event: e });
            }
        };
        var viewTimeout;
        scope.ngKeyUp = function (e) {
            scope.typing = true;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);
            clearInterval(viewTimeout);
            viewTimeout=setInterval(function(){
				clearInterval(viewTimeout);
				updateView();
			},1500);
            scope.onKeyUp({ $event: e });
        };

        scope.ngKeyDown = function (e) {
            scope.typing = true;
            smartTextFieldService.modal.setModal(scope, e.target.value, ngModelCtrl);
            updateView();
            scope.onKeyDown({ $event: e });
        };

        scope.ngFocus = function (e) {
            scope.onFocus({ $event: e });
        };

        scope.prevModel = smartTextFieldService.modal.getModal(scope, ngModelCtrl);
        var updateView = function () {
            try {
                if (smartTextFieldService.modal.getModal(scope, ngModelCtrl) !== scope.prevModel) {

                    /* Removed $timeout due to ngBlur gets fired before scope.ngModel */
                    $timeout(function () {
                        scope.prevModel = smartTextFieldService.modal.getModal(scope, ngModelCtrl);

                        if (typeof scope.ngModel === 'function') {
                            smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                        }
                    });
                    //scope.$apply();
                }
                    /* uncommented next line for PPST2-2193 (same text not searched again) */
                else if (scope.isSearch == "true") {
                    ngModelCtrl.$setViewValue(scope.ngModel);
                }
                smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
            }
            catch (e) { }
        };

        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateTextValue(scope, element);
        //    };
        //}
        //catch (e) { }
        var unbindModelWatcher;
        if (typeof scope.ngModel != 'function') {
            unbindModelWatcher = scope.$watch('ngModel', function (newVal) {
                scope.isActive = smartTextFieldService.checkIfActive(scope, element);

                smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
                smartTextFieldService.setActiveClass(scope, element, ngModelCtrl);
                smartTextFieldService.updateTextFieldClass(scope, element);
                if (scope.type === 'area') {
                    $timeout(function () {
                        angular.element('#' + scope.textfieldId).trigger('autoresize');
                    });
                }
            });
        } else {
            unbindModelWatcher = scope.$watch(function () { return scope.ngModel(); }, function (newVal, oldVal) {
                if (newVal != oldVal) {
                    scope.isActive = smartTextFieldService.checkIfActive(scope, element);

                    smartTextFieldService.updateTextValue(scope, element, ngModelCtrl);
                    smartTextFieldService.setActiveClass(scope, element, ngModelCtrl);
                    smartTextFieldService.updateTextFieldClass(scope, element);
                    if (scope.type === 'area') {
                        $timeout(function () {
                            angular.element('#' + scope.textfieldId).trigger('autoresize');
                        });
                    }
                }

            }, true);
        }

        //var unbindAttributesWatcher = scope.$watchGroup(['isMandatory', 'disable', 'readonly', 'focus', 'validate', 'isRemovable'], function (newVal, oldVal) {

        //    if (newVal[4] != oldVal[4]) {
        //        smartTextFieldService.validateRules(scope, ngModelCtrl, element,attrs);
        //    }
        //    smartTextFieldService.setActiveClass(scope, element);
        //    smartTextFieldService.updateTextFieldClass(scope, element);


        //});

        var unbindOtherWatcher;
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element, ngModelCtrl);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();
            }

        });
        unbindOtherWatcher = scope.$watchGroup(['isMandatory', 'focus', 'validate', 'isRemovable', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
            if (newVal[2] != oldVal[2] || newVal[4] != oldVal[4]) { //validate
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);

                smartTextFieldService.setActiveClass(scope, element, ngModelCtrl);
                smartTextFieldService.updateTextFieldClass(scope, element);
            }
            if (newVal[5] != oldVal[5] || newVal[6] != oldVal[6]) {
                if (scope.showinfo || scope.showwarning) {
                    element.find('> .input-field').addClass('field-has-icon');
                }
                else {
                    element.find('> .input-field').removeClass('field-has-icon');
                }
            }
            if (newVal[1] || newVal[1] == "true") {
                element.find('input').focus();
            }
        });

        //function bindOtherWatcher() {
        //    if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
        //        //if (!angular.isDefined(unbindOtherWatcher)) {

        //        //}
        //    }
        //    else {
        //        if (angular.isDefined(unbindOtherWatcher)) {
        //            unbindOtherWatcher();
        //        }
        //    }
        //}
        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };
        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange, attrs, ngModelCtrl);
        updateView();

        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }

        //bindOtherWatcher();
        //setTimeout(function () {
        //    //if (scope.readonly == 'true') {
        //    //    angular.element('#' + scope.textfieldId).attr('tabindex', -1);
        //    //}
        //    try {
        //        if (angular.isDefined(scope.$parent.$parent.onEditableRender)) {
        //            scope.$parent.$parent.onEditableRender(scope, element);
        //        }
        //    }
        //    catch (e) { }
        //});

        scope.triggerSearchEvent = function (e) {
            scope.elmVal = e.target.value;
            scope.onSearch({
                $event: {
                    data: element.find('#' + scope.textfieldId),
                    displayModel: e.target.value
                }
            });
        }

        smartTextFieldService.assignSearchEvents(scope, element);

        scope.$on('$destroy', function () {
            unbindModelWatcher();
            unbindAttributesWatcher();
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
        });
    }

    function SmartTextField3_DateFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate) {
        if (scope.timePicker === 'true') {
            scope.timePicker = true;
        }

        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null || scope.placeholder == "" ? moment.localeData()._longDateFormat.L : scope.placeholder;
        if (scope.timePicker == true) {
            if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
                scope.placeholder = scope.regionalFormat ? scope.regionalFormat : moment.localeData()._longDateFormat.L + ' ' + moment.localeData()._longDateFormat.LT;
            }
            else {
                scope.placeholder = moment.localeData()._longDateFormat.L + ' ' + moment.localeData()._longDateFormat.LT;
            }
        }

        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        scope.datetimePicker = (typeof scope.timePicker == undefined ? false : scope.timePicker === true ? true : false);
        scope.format = (scope.format ? scope.format : scope.datetimePicker ? 'DD/MM/YYYY h:mm' : 'DD/MM/YYYY');
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
          //  following line of code has been added for pendo
          var tmpId = '';
          if(attrs.id){
              tmpId = "tf_" + attrs.id;        
              var tmpElementCount = $('#' + tmpId).length;
              if(tmpElementCount > 0) {
                  tmpId = tmpId + '_' + textfieldId;
              }
              element.removeAttr('id');
          }else {
              tmpId = 'tf_' + textfieldId;
          }    
          scope.textfieldId = tmpId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

        scope.applyClientZone = false;
        scope.isValueDateObj = false;
        scope.doesContainDateString = false;
        scope.dateFormat = scope.format;
        scope.isDateBlank = false;

        if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
            if (scope.regionalFormat) {
                scope.dateFormat = scope.regionalFormat;
            }
            else {
                if (scope.timePicker == true) {
                    scope.dateFormat = moment.localeData().longDateFormat('L') + ' ' + moment.localeData().longDateFormat('LT');
                }
                else {
                    scope.dateFormat = moment.localeData().longDateFormat('L');
                }
            }
            scope.format = scope.dateFormat;
        }

        scope.convertIntoUTC = false;
        if (typeof convertIntoUTC != 'undefined') {
            scope.convertIntoUTC = convertIntoUTC;
        }

        //if (angular.isDefined(scope.$parent.$parent.property)) {
        //    //this property (applyClientZone) comes from Config.js
        //    if (angular.isDefined(scope.$parent.$parent.property.applyClientZone)) {
        //        scope.applyClientZone = scope.$parent.$parent.property.applyClientZone;
        //    }
        //}



        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }



        scope.onTextChange = function (e) {
            //scope.ngModel = e.target.value;
            // smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.setActiveClass(scope, element);
            // if (rulesValidationEvent == 'change') {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.updateTextFieldClass(scope, element);
            //  }
            scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {
            if (scope.isDateBlank) {
                var targetedEle = document.activeElement;
                if (!(angular.element(targetedEle).hasClass('monthselect') || angular.element(targetedEle).hasClass('yearselect') || angular.element(targetedEle).hasClass('hourselect') || angular.element(targetedEle).hasClass('minuteselect'))) {
                    //  scope.ngModel = null;
                    scope.dateModel = null;
                }
                if (angular.isFunction(scope.onDateChange)) {
                    scope.onDateChange({
                        $date: scope.ngModel
                    });
                }
            }

            var selectedDate = smartTextFieldService.getSelectedDate();
            if (selectedDate == '') {
                $('#' + scope.textfieldId).val('');
            }
            if ($('#' + scope.textfieldId).find('input').daterangepicker())
                $('#' + scope.textfieldId).find('input').daterangepicker().daterangepicker();
            if (angular.isDefined(selectedDate) && scope.datetimePicker && selectedDate.indexOf('Date(') <= -1)
                $('#' + scope.textfieldId).val(smartTextFieldService.getPreviousDate());
            smartTextFieldService.setSelectedDate(undefined);
            scope.focus = false;
            //smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);




            scope.onBlur({ $event: e });
        }

        scope.ngKeyUp = function (e) {
            if (e.which != 32) {
                var currentInputValue = $('#' + scope.textfieldId).val();
                smartTextFieldService.setSelectedDate($('#' + scope.textfieldId).val());
                if (currentInputValue == "") {
                    scope.ngModel = null;
                    scope.dateModel = ""
                    scope.isDateBlank = true;
                    if (angular.isFunction(scope.onDateChange)) {
                        scope.onDateChange({
                            $date: scope.ngModel
                        });
                    }
                }
            }

            if (rulesValidationEvent == 'keyUp') {
                scope.validateRules();
            }
            scope.onKeyUp({ $event: e });
        };

        scope.ngKeyDown = function (e) {
            if (rulesValidationEvent == 'keyDown') {
                scope.validateRules();
            }
            ////to control number of down arrow,up arrow signs on number fields
            //if (e.keyCode == 38 || e.keyCode == 40)
            //    e.preventDefault();
            scope.onKeyDown({ $event: e });
        };

        scope.ngKeyPress = function (e) {

            if (rulesValidationEvent == 'keyPress') {
                scope.validateRules();
            }
            scope.onKeyPress({ $event: e });
            if (e.charCode == 13) {
                scope.onEnter({ $event: e });
            }
        };





        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateDateValue(scope, element);
        //    };
        //}
        //catch (e) { }

        scope.prevModel = scope.ngModel;
        var updateView = function () {
            try {
                if (scope.ngModel !== scope.prevModel) {
                    $timeout(function () {
                        scope.prevModel = scope.ngModel;
                    });

                }
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateDateValue(scope, element);
            }
            catch (e) { }
        };



        //var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {


        //});


        var unbindOtherWatcher;
        var unbindMin;
        var unbindMax
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();
            }

        });

        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };

        function bindOtherWatcher() {
            if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
                if (!angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher = scope.$watchGroup(['isMandatory', 'focus', 'validate', 'isRemovable', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
                        if (newVal[2] != oldVal[2] || newVal[4] != oldVal[4]) { //validate
                            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);

                            smartTextFieldService.setActiveClass(scope, element);
                            smartTextFieldService.updateTextFieldClass(scope, element);
                        }

                        if (newVal[1] || newVal[1] == "true") {
                            element.find('input').focus();
                        }

                        if (newVal[5] != oldVal[5] || newVal[6] != oldVal[6]) {
                            if (scope.showinfo || scope.showwarning) {
                                element.find('> .input-field').addClass('field-has-icon');
                            }
                            else {
                                element.find('> .input-field').removeClass('field-has-icon');
                            }
                        }
                    });
                }
                if (!angular.isDefined(unbindMin)) {
                    unbindMin = scope.$watch('min', function (n) {
                        smartTextFieldService.createDatePicker(scope, element, ngModelCtrl);
                    });
                }
                if (!angular.isDefined(unbindMax)) {
                    unbindMax = scope.$watch('max', function (n) {
                        smartTextFieldService.createDatePicker(scope, element, ngModelCtrl);
                    });
                }

            }
            else {
                if (angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher();
                }
                if (angular.isDefined(unbindMin)) {
                    unbindMin();
                }
                if (angular.isDefined(unbindMax)) {
                    unbindMax();
                }
            }
        }




        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange);
        smartTextFieldService.updateDateClass(scope, element);
        smartTextFieldService.assignDateEvents(scope, element);
        bindOtherWatcher();
        var unbindDateModel = scope.$watch('dateModel', function (newVal) {
            if (newVal) {
                scope.isActive = smartTextFieldService.checkIfActive(scope, element);
                if (rulesValidationEvent == 'change') {
                    scope.validateRules();
                }
                smartTextFieldService.updateDateValue(scope, element);
            }
        });
        var timeOffset = 20; // this is a fixed number in-case of timeZoneOffset is not defined.
        scope.updatingDateModel = function (dateObj) {
            if (typeof timeZoneOffset != 'undefined') {
                timeOffset = timeZoneOffset;
            }

            if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable && scope.regionalFormat) {
                timeOffset = 0;
            }

            if (timeOffset == 20) {
                var adjustTimeZone = 0;
                if (moment().zone() == -720) {
                    adjustTimeZone = -1;
                }
                scope.dateModel = moment(dateObj).add(adjustTimeZone, 'second').format(scope.dateFormat);
            }
            else {
                var adjustTimeZone = 0;
                if (timeOffset == -12) {
                    adjustTimeZone = -1;
                }

                if (typeof dateObj == 'object') {
                    if (timeZoneOffset != 20) {
                        scope.dateModel = moment(dateObj).add(timeOffset * 60, 'minute').add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                    else {
                        scope.dateModel = moment(dateObj).add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                } else {
                    if (timeZoneOffset != 20) {
                        scope.dateModel = moment(dateObj).utc().zone(timeOffset * -1).add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                    else {
                        scope.dateModel = moment(dateObj).utc().add(adjustTimeZone, 'second').format(scope.dateFormat);
                    }
                    //scope.dateModel = moment(dateObj).zone(timeOffset * -1).utc().add(adjustTimeZone, 'second').format(scope.dateFormat);
                }
            }

        }
        // should be modified to discard UTC Convertion 
        var unbindNgModel = scope.$watch('ngModel', function (newVal) {
            if (scope.timePicker === 'true') {
                scope.timePicker = true;
            }
            if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable && scope.regionalFormat) {
                $timeout(function () {
                    scope.dateModel = element.find('input').val();
                }, 250);
            }
            if (!scope.dateModifiedbyUser) {
                if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
                    if (scope.regionalFormat) {
                        scope.dateFormat = scope.regionalFormat;
                    }
                    else {
                        if (scope.timePicker == true) {
                            scope.dateFormat = moment.localeData().longDateFormat('L') + ' ' + moment.localeData().longDateFormat('LT');
                        }
                        else {
                            scope.dateFormat = moment.localeData().longDateFormat('L');
                        }
                    }
                    scope.dateFormat = scope.dateFormat.replace(/d/g, 'D').replace(/y/g, 'Y').replace(/E/g, 'd');
                    scope.format = scope.dateFormat;
                }
                else {
                    scope.dateFormat = scope.format.replace(/d/g, 'D').replace(/y/g, 'Y').replace(/E/g, 'd');
                }

                scope.isDateBlank = false;
                scope.doesContainDateString = scope.setFromGrid == 'true' ? true : (newVal + '').toLowerCase().indexOf('date') > -1 ? true : false;
                if (newVal == "" || newVal == null || typeof newVal == 'function') {
                    newVal = Date.now();
                    scope.isDateBlank = true;
                    scope.isValueDateObj = false;
                } else {
                    if (scope.doesContainDateString) {
                        var dateStr = (newVal + '').replace(new RegExp("/", "ig"), '');
                        var tmpModel = dateStr;
                        if (dateStr.indexOf('Date') <= -1) {
                            tmpModel = tmpModel.split('+');
                            tmpModel = eval('new ' + (tmpModel.length > 1 ? ('Date("' + newVal + '").getTime()') : tmpModel[0]));
                        }
                        scope.updatingDateModel(tmpModel);
                        //scope.dateModel = moment(tmpModel).format(scope.dateFormat);
                        //smartTextFieldService.updateDateValue(scope, element);
                    }
                    else {
                        if (Object.prototype.toString.call(newVal) === '[object Date]') {
                            scope.isValueDateObj = true;
                        } else if (Object.prototype.toString.call(newVal) === '[object String]' && newVal.indexOf('T') > 0) {
                            //scope.isValueDateObj = true;
                        } else {
                            newVal = parseInt(newVal);
                        }
                    }
                }

                scope.placeholder = scope.format;
                //scope.placeholder = scope.placeholder == undefined || scope.placeholder == null || scope.placeholder == "" ? 'DD/MM/YYYY' : scope.placeholder; // to consider custom placeholder value and not only the format of the date-time picker

                if (!scope.isDateBlank) {
                    if (!scope.doesContainDateString) {
                        //if (scope.applyClientZone) {
                        //    scope.dateModel = moment(newVal).parseZone().format(scope.dateFormat);
                        //}
                        //else {
                        scope.updatingDateModel(newVal);
                        //scope.dateModel = moment(newVal).format(scope.dateFormat);
                        //}
                    }
                } else {
                    scope.dateModel = "";
                }
                smartTextFieldService.updateDateValue(scope, element);
                smartTextFieldService.createDatePicker(scope, element, ngModelCtrl);
            }
            scope.isActive = smartTextFieldService.checkIfActive(scope, element);
            scope.dateModifiedbyUser = false;

            //smartTextFieldService.updateDateValue(scope, element);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);

        });

        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }

        scope.$on('$destroy', function () {
            //unbindModelWatcher();
            unbindAttributesWatcher();
            unbindNgModel();
            unbindDateModel();
            if (angular.isDefined(unbindMin)) {
                unbindMin();
            }
            if (angular.isDefined(unbindMax)) {
                unbindMax();
            }
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
        });
    }

    function SmartTextField3_AutocompleteFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $translate, $window) {
        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
        var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
        showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
          //  following line of code has been added for pendo
          var tmpId = '';
          if(attrs.id){
              tmpId = "tf_" + attrs.id;        
              var tmpElementCount = $('#' + tmpId).length;
              if(tmpElementCount > 0) {
                  tmpId = tmpId + '_' + textfieldId;
              }
              element.removeAttr('id');
          }else {
              tmpId = 'tf_' + textfieldId;
          }    
          scope.textfieldId = tmpId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);

        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }



        scope.onTextChange = function (e) {

            console.log(scope.displayModel);

            //updateView();

            //console.log(scope.displayModel);
            scope.onChange({
                $event: {
                    data: element.find('#' + scope.textfieldId),
                    displayModel: scope.displayModel
                }
            });

            ////scope.ngModel = e.target.value;
            //smartTextFieldService.updateAutocompleteValue (scope, element);
            //smartTextFieldService.setActiveClass(scope, element);
            //// if (rulesValidationEvent == 'change') {
            //smartTextFieldService.validateRules(scope, ngModelCtrl, element);
            //smartTextFieldService.updateTextFieldClass(scope, element);
            ////  }
            ////scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {


            if (scope.ngModel && scope.displayformat) {
                var splittedDisplayFormat = [];
                if (scope.displayformat.indexOf(":") > 0)
                    splittedDisplayFormat = scope.displayformat.split(':');
                else if (scope.displayformat.indexOf("-") > 0)
                    splittedDisplayFormat = scope.displayformat.split('-');
                else
                    splittedDisplayFormat = scope.displayformat.split('-');
                var result = "";
                for (var i = 0; i < splittedDisplayFormat.length; i++) {
                    result += eval("scope.ngModel." + splittedDisplayFormat[i].replace('{', '').replace('}', ''));
                    if (i < splittedDisplayFormat.length - 1) {
                        if (scope.displayformat.indexOf("-") > 0)
                            result += '-';
                        else if (scope.displayformat.indexOf(":") > 0)
                            result += ':';
                    }
                }

                // P2P-16866
                if (scope.retainDisplayModelAutocomplete !== "true") {

                    if (e.target.value.trim() == '' || result !== scope.displayModel) {
                        scope.displayModel = '';
                    }
                }

            }


            scope.focus = false;
            smartTextFieldService.updateAutocompleteValue(scope, element);
            $timeout(function () {
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            });
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);

            try {
                if (angular.isDefined(scope.$parent.$parent.onStopEdit)) {
                    scope.$parent.$parent.onStopEdit(scope, element);
                }
            }
            catch (e) { }



            scope.onBlur({ $event: e });
        }

        scope.ngKeyPress = function (e) {
            ////console.log("The text has been changed.");
            ////updateView();

            ////var val = e.target.value;

            //scope.displayModel = e.target.value;

            if (rulesValidationEvent == 'keyPress') {
                scope.validateRules();
            }
            scope.onKeyPress({ $event: e });
            if (e.charCode == 13) {
                scope.onEnter({ $event: e });
            }


        };
        scope.elmVal = null;
        scope.ngKeyUp = function (e) {
            ////console.log("The text has been changed.");
            ////updateView();

            //if (e.target.value != '' && e.target.value != scope.elmVal) {
            //    scope.elmVal = e.target.value;
            //    scope.onChange({
            //        $event: {
            //            data: element.find('#' + scope.textfieldId),
            //            displayModel: e.target.value
            //        }
            //    });
            //}




        };

        scope.search = function (e) {
            if (e.target.value != '' && e.target.value != scope.elmVal) {
                scope.elmVal = e.target.value;
                scope.onChange({
                    $event: {
                        data: element.find('#' + scope.textfieldId),
                        displayModel: e.target.value
                    }
                });
            }
        };

        scope.ngFocus = function (e) {
            var elm = element.find('#' + scope.textfieldId);
            var Obj = elm.devbridgeAutocomplete();
            if (elm.val() == '' && Obj) {
                Obj.clear();
                scope.elmVal = '';
            }
            scope.onFocus({ $event: e });
        }


        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateAutocompleteValue(scope, element);
        //    };
        //}
        //catch (e) { }

        scope.prevModel = scope.ngModel;
        var updateView = function () {
            try {
                if (scope.ngModel !== scope.prevModel) {
                    $timeout(function () {
                        scope.prevModel = scope.ngModel;
                    });

                }
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateAutocompleteValue(scope, element);
            }
            catch (e) { }
        };



        var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

            scope.displayModel = smartTextFieldService.getDisplayData(newVal, scope);

            scope.isActive = smartTextFieldService.checkIfActive(scope, element);     


            smartTextFieldService.updateAutocompleteValue(scope, element);
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);
        });

        var unbindOtherWatcher;
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();

            }

        });

        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };

        function bindOtherWatcher() {
            if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
                if (!angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher = scope.$watchGroup(['focus', 'isRemovable'], function (newVal, oldVal) {
                        smartTextFieldService.setActiveClass(scope, element);
                        smartTextFieldService.updateTextFieldClass(scope, element);
                    });
                }
            }
            else {
                if (angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher();
                }
            }
        }


        var unbindValidationWatcher = scope.$watchGroup(['isMandatory', 'validate', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
            if (newVal[1] != oldVal[1] || newVal[2] != oldVal[2]) {
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            }
            if (newVal[3] != oldVal[3] || newVal[4] != oldVal[4]) {
                if (scope.showinfo || scope.showwarning) {
                    element.find('> .input-field').addClass('field-has-icon');
                }
                else {
                    element.find('> .input-field').removeClass('field-has-icon');
                }
            }
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);
        });







        // key to be used to bind options
        var filterKeys;
        if (angular.isDefined(scope.filterkeys) && scope.filterkeys.trim().indexOf('[') == 0) {
            filterKeys = scope.filterkeys ? scope.$eval(scope.filterkeys) : ['title'];
        }
        else {
            filterKeys = scope.filterkeys ? scope.$eval('[' + scope.filterkeys + ']') : ['title'];
        }
        var optionFormat = scope.optionformat ? scope.optionformat : '{title}';
        scope.ndisplayFormat = scope.displayformat ? scope.displayformat : '{title}';

        /*
         * Check if the options are updated
         */
        var autoCompleteField;
        var unbindOptions = scope.$watch('options', function (newOptions) {
            try {
                if (!$('#' + scope.textfieldId).devbridgeAutocomplete()) {
                    autoCompleteField = $('#' + scope.textfieldId).devbridgeAutocomplete({
                        filterKeys: filterKeys,
                        optionFormat: optionFormat,
                        displayFormat: scope.ndisplayFormat,
                        lookup: smartTextFieldService.getFormattedData(newOptions, scope),
                        width: scope.width,
                        showNoSuggestionNotice: showNoSuggestion,
                        noSuggestionNotice: noSuggestionNotice,
                        minChars: angular.isDefined(scope.minchars) ? parseInt(scope.minchars) : 1,
                        onSelect: function (suggestion) {
                        angular.element(document).trigger('click');
                        scope.$evalAsync(function () {
                                scope.displayModel = smartTextFieldService.getDisplayData(suggestion, scope);
                                scope.ngModel = suggestion;
                            updateView();
                            $timeout(function () {
                                scope.onSelect({
                                    $event: {
                                            data: suggestion
                                    }
                                });
                            });
                        });
                    }
                    }).devbridgeAutocomplete();
                }
                else {
                    $('#' + scope.textfieldId).devbridgeAutocomplete().setLookup(smartTextFieldService.getFormattedData(newOptions, scope));
                }
            }
            catch (e) { }
        });



        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange, attrs);
        smartTextFieldService.assignAutocompleteEvents(scope, element);
        bindOtherWatcher();

        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }

        //Closing the suggestions on scroll to prevent overlapping issue
        angular.element($window).scroll(function (e) {
            hideAutoOnScroll();
        });

        angular.element(".modal, .modal-content").scroll(function (e) {
            hideAutoOnScroll();
        });

        function hideAutoOnScroll() {
            if (autoCompleteField != undefined) {
                if (autoCompleteField.visible) {
                    autoCompleteField.hide();
                }
                            }
            }

        scope.$on('$destroy', function () {
            unbindModelWatcher();
            unbindAttributesWatcher();
            unbindOptions();
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
            unbindValidationWatcher();
        });
    }
        
    function smartTextField3_NumFunc(scope, element, attrs, ngModelCtrl, smartTextFieldService, $timeout, $filter, $translate) {
        scope.placeholder = scope.placeholder == undefined || scope.placeholder == null ? '' : scope.placeholder;
        scope.ngModel = scope.ngModel == undefined || scope.ngModel == null ? '' : scope.ngModel;
        scope.textAlign = scope.align == undefined || scope.align == null ? 'left' : scope.align;
        scope.suffixicon = ((scope.suffixicon == undefined || scope.suffixicon == null) && scope.type == "date") ? "#icon_Calendar" : scope.suffixicon; //  date picker icon                    
        scope.count = 0;
        var noSuggestionNotice = angular.isDefined(scope.noSuggestionNotice) ? scope.noSuggestionNotice : $translate.instant('noResultsErrMsg');
        var showNoSuggestion = angular.isDefined(scope.showNoSuggestion) ? (scope.showNoSuggestion == 'true') ? true : false : false;
        showNoSuggestion = (noSuggestionNotice == '') ? false : showNoSuggestion;
        var number, rulesValidationEvent;
        scope.type = angular.isDefined(scope.type) ? scope.type : 'text';
        textfieldId++;
        scope.textfieldId = 'tf_' + textfieldId;
          //  following line of code has been added for pendo
          var tmpId = '';
          if(attrs.id){
              tmpId = "tf_" + attrs.id;        
              var tmpElementCount = $('#' + tmpId).length;
              if(tmpElementCount > 0) {
                  tmpId = tmpId + '_' + textfieldId;
              }
              element.removeAttr('id');
          }else {
              tmpId = 'tf_' + textfieldId;
          }    
          scope.textfieldId = tmpId;
        scope.isRemovable = scope.$eval(scope.removable) == undefined ? true : scope.$eval(scope.removable);
        scope.showTitle = scope.isTitleVisible == undefined || scope.isTitleVisible == null || scope.isTitleVisible == 'true' ? true : false;
        scope.infinite = attrs.infinite == 'true' ? true : false;

        if (attrs.validateOn) {
            rulesValidationEvent = attrs.validateOn;
        }
        else {
            rulesValidationEvent = 'blur';
        }
        var preDecimalLength="";
		if(typeof scope.max != "undefined"){
			for(var i=0; i < scope.max; i++){
				preDecimalLength+="9";
			}
		}else{
			preDecimalLength=Number.POSITIVE_INFINITY;
		}



         scope.onTextChange = function (e) {

            // if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
            var num = element.find('#' + scope.textfieldId).autoNumeric('get');
            if (num != '') {
                scope.ngModel = parseFloat(element.find('#' + scope.textfieldId).autoNumeric('get'));
            }
            else {
                scope.ngModel = null;
            }
            //}
            //else {
            //    scope.ngModel = parseFloat(e.target.value);
            //}
            updateView();
            //scope.ngModel = parseFloat(e.target.value);
            //smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.setActiveClass(scope, element);
            // if (rulesValidationEvent == 'change') {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.updateTextFieldClass(scope, element);
            //  }
            scope.onChange({ $event: e });
        };

        scope.ngBlur = function (e) {
            //if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {

            var num = element.find('#' + scope.textfieldId).autoNumeric('get');
            if (num != '') {
                scope.ngModel = parseFloat(element.find('#' + scope.textfieldId).autoNumeric('get'));
            }
            else {
                scope.ngModel = null;
            }
            //}
            //else {
            //    var num = e.target.value;
            //    if (num.trim() != '') {
            //        scope.ngModel = parseFloat(num);
            //    }
            //    else {
            //        scope.ngModel = null;
            //    }
            //}
            updateView();
            scope.focus = false;
            //smartTextFieldService.updateTextValue(scope, element);
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);




            scope.onBlur({ $event: e });
        }

        scope.ngKeyPress = function (e) {
            if (scope.type == 'number' || attrs.datanumeric) {
                //to control number of +,-, signs and alphabets on number fields
                var charCode = e.charCode || e.keyCode,
                    textValue = e.currentTarget.value,
                    decimalDigit = [],
                    cc = String.fromCharCode(charCode),
                    escapeChars = [37, 38, 39, 40, 46, 8];

                if (isNaN(cc) && (escapeChars.indexOf(charCode) == -1)) {
                    e.preventDefault();
                }
                // Restrict the 2 dots entering
                if (textValue.toString().indexOf('.') !== -1 && charCode == 46) {
                    e.preventDefault();
                }

                if (textValue) {
                    decimalDigit = textValue.toString().split('.');
                }
                // Restrict the decimal value from entering
                if (attrs.decimalprecision) {
                    if (charCode == 46 && parseInt(attrs.decimalprecision) == 0) {
                        e.preventDefault();
                    }
                    if (decimalDigit.length == 2) {
                        if (decimalDigit[1] && decimalDigit[1].length >= parseInt(attrs.decimalprecision, 10)) {
                            e.preventDefault();
                        }
                    }
                }
                if (scope.min != undefined || scope.min != null) {
                    if (scope.min >= 0 && e.charCode == 45) {
                        e.preventDefault();
                    }
                }
            }

            if (rulesValidationEvent == 'keyPress') {
                smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
            }
            scope.onKeyPress({ $event: e });
            if (e.charCode == 13) {
                scope.onEnter({ $event: e });
            }

            // because of debounce has applied on smart-textfield, here we need to do DOM manipulation i.e. e.target.value to get latest value from textfield
            if (angular.isDefined(attrs.maxlength) && e.target.value.length == attrs.maxlength && e.charCode !== 0) {
                e.preventDefault();
            }
        };




        /*
     *  Following code has been added to make ng-model-options work
     *  ngModelCtrl.$render & ngModelCtrl.$setViewValue serve the purpose
     */
        //try {
        //    ngModelCtrl.$render = function () {
        //        scope.ngModel = ngModelCtrl.$modelValue;
        //        smartTextFieldService.updateTextValue(scope, element);
        //    };
        //}
        //catch (e) { }

        scope.prevModel = scope.ngModel;
        var updateView = function () {
            try {
                //ngModelCtrl.$setViewValue(scope.ngModel);
                smartTextFieldService.updateTextValue(scope, element);
                //smartTextFieldService.updateTextValue(scope, element);
                if (scope.ngModel != null) {
                    var min = 0;
                    var max = 0;
                    if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
                        if (scope.minmaxprecision == undefined || scope.minmaxprecision == '') {
                            min = 0;
                            max = 0;
                        }
                        else {
                            min = MinPrecessionValue;//global default
                            max = MaxPrecessionValue;//global default
                        }
                    }
                    else {
                        if (scope.minmaxprecision !== undefined && scope.minmaxprecision !== '') {
                            var splitPrecision = JSON.parse(scope.minmaxprecision);
                            if (splitPrecision.length == 2) {
                                min = splitPrecision[0];
                                max = splitPrecision[1];
                            }
                        }
                    }
                    try {
                        if (scope.minmaxprecision) {
                            min = JSON.parse(scope.minmaxprecision)[0];
                            max = JSON.parse(scope.minmaxprecision)[1];
                        }
                    }
                    catch (e) {
                        console.log(e)
                    }
                    var valueToModel;
                    if (scope.ngModel === '') {
                        valueToModel = scope.ngModel;
                    } else {
                        valueToModel = $filter('minPrecisionHandler')(scope.ngModel, min, max, false);
                    }
                    element.find('#' + scope.textfieldId).val(valueToModel);
                    if (!scope.ngModel) {
                        var num = element.find('#' + scope.textfieldId).autoNumeric('get');
                        if (num != '') {
                            scope.ngModel = parseFloat(element.find('#' + scope.textfieldId).autoNumeric('get'));
                            smartTextFieldService.setActiveClass(scope, element);
                            smartTextFieldService.updateTextFieldClass(scope, element);
                        }
                        else {
                            scope.ngModel = null;
                        }
                    }

                    if (scope.ngModel !== scope.prevModel) {
                        $timeout(function () {
                            scope.prevModel = scope.ngModel;
                        });
                    }


                }
            }
            catch (e) { }
        };



        var unbindModelWatcher = scope.$watch('ngModel', function (newVal) {

            scope.isActive = smartTextFieldService.checkIfActive(scope, element);


            updateView();
            smartTextFieldService.setActiveClass(scope, element);
            smartTextFieldService.updateTextFieldClass(scope, element);
        });


        //var unbindAttributesWatcher = scope.$watchGroup(['isMandatory', 'disable', 'readonly', 'focus', 'validate', 'isRemovable'], function (newVal, oldVal) {

        //    if (newVal[4] != oldVal[4]) {
        //        smartTextFieldService.validateRules(scope, ngModelCtrl, element,attrs);
        //    }
        //    smartTextFieldService.setActiveClass(scope, element);
        //    smartTextFieldService.updateTextFieldClass(scope, element);


        //});

        var unbindOtherWatcher;
        var unbindAttributesWatcher = scope.$watchGroup(['disable', 'readonly'], function (newVal, oldVal) {
            if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1]) {
                smartTextFieldService.setActiveClass(scope, element);
                smartTextFieldService.updateTextFieldClass(scope, element);
                bindOtherWatcher();
            }

        });


        function bindOtherWatcher() {
            if ((angular.isDefined(scope.disable) && (scope.disable == false || scope.disable == 'false')) || ((angular.isDefined(scope.readonly) && (scope.readonly == false || scope.readonly == 'false')))) {
                if (!angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher = scope.$watchGroup(['isMandatory', 'focus', 'validate', 'isRemovable', 'clienterror', 'showinfo', 'showwarning'], function (newVal, oldVal) {
                        if (newVal[2] != oldVal[2] || newVal[4] != oldVal[4]) { //validate
                            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);

                            smartTextFieldService.setActiveClass(scope, element);
                            smartTextFieldService.updateTextFieldClass(scope, element);
                        }
                        if (newVal[5] != oldVal[5] || newVal[6] != oldVal[6]) {
                            if (scope.showinfo || scope.showwarning) {
                                element.find('> .input-field').addClass('field-has-icon');
                            }
                            else {
                                element.find('> .input-field').removeClass('field-has-icon');
                            }
                        }
                        if (newVal[1] || newVal[1] == "true") {
                            element.find('input').focus();
                        }
                    });
                }
            }
            else {
                if (angular.isDefined(unbindOtherWatcher)) {
                    unbindOtherWatcher();
                }
            }
        }




        scope.hideShowField = function () {
            scope.isVisible = !scope.isVisible;
        };



        smartTextFieldService.buildTemplate(scope, element, scope.onTextChange);
        if (typeof RegionalSettingEnable != "undefined" && RegionalSettingEnable) {
            if (scope.minmaxprecision == undefined || scope.minmaxprecision == '') {
                element.find('#' + scope.textfieldId).autoNumeric('init', {
                    mDec: 0,
                    aPad: false,
                    aSep: thousandSep,
                    aDec: decimalSep,
                    vMin: Number.NEGATIVE_INFINITY,
                    vMax: preDecimalLength
                });
            } else {
                //element.find('#' + scope.textfieldId).autoNumeric();
                var highPrecisionLimit = 0;
				if (scope.minmaxprecision !== undefined && scope.minmaxprecision !== '') {
					var splitPrecision = JSON.parse(scope.minmaxprecision);
					if (splitPrecision.length == 2) {
						var lowerPrecisionLimit = splitPrecision[0];
						highPrecisionLimit = splitPrecision[1];
					}
				}
				element.find('#' + scope.textfieldId).autoNumeric('init', {
					mDec: highPrecisionLimit,
					aPad: false,
					aSep: thousandSep,
                    aDec: decimalSep,
					vMin: Number.NEGATIVE_INFINITY,
					vMax: preDecimalLength,
				});
            }
        }
        else {
            var thousandSep = ',';
            var decimalSep = '.';
            var highPrecisionLimit = 0;
            if (scope.minmaxprecision !== undefined && scope.minmaxprecision !== '') {
                var splitPrecision = JSON.parse(scope.minmaxprecision);
                if (splitPrecision.length == 2) {
                    var lowerPrecisionLimit = splitPrecision[0];
                    highPrecisionLimit = splitPrecision[1];
                }
            }
            element.find('#' + scope.textfieldId).autoNumeric('init', {
                mDec: highPrecisionLimit,
                aPad: false,
                aSep: thousandSep,
                aDec: decimalSep,
                vMin: Number.NEGATIVE_INFINITY,
                vMax: preDecimalLength,
            });
        }
        bindOtherWatcher();
        if (scope.clienterror) {
            smartTextFieldService.validateRules(scope, ngModelCtrl, element, attrs);
        }
        //setTimeout(function () {
        //    //if (scope.readonly == 'true') {
        //    //    angular.element('#' + scope.textfieldId).attr('tabindex', -1);
        //    //}
        //    try {
        //        if (angular.isDefined(scope.$parent.$parent.onEditableRender)) {
        //            scope.$parent.$parent.onEditableRender(scope, element);
        //        }
        //    }
        //    catch (e) { }
        //});

        scope.$on('$destroy', function () {
            unbindModelWatcher();
            unbindAttributesWatcher();
            if (angular.isDefined(unbindOtherWatcher)) {
                unbindOtherWatcher();
            }
        });
    }




    angular.module('SMART2').factory('smartTextFieldService', ['ScrollTo', 'RuleEngine', '$templateCache', '$compile', '$translate', '$timeout', 'formWidgetUtils', smartTextFieldServiceFunc])
    function smartTextFieldServiceFunc(ScrollTo, RuleEngine, $templateCache, $compile, $translate, $timeout, formWidgetUtils) {
        var checkIfActive = function (scope) {
            if (typeof scope.readonly !== 'undefined' && scope.readonly == 'true') {
                return true;
            }
            if (scope.type == 'number' && scope.ngModel !== 0 + '' && (typeof scope.placeholder !== 'undefined' && scope.placeholder === '') && (typeof scope.ngModel === 'undefined' || scope.ngModel === '' || isNaN(scope.ngModel) || scope.ngModel === undefined || scope.ngModel === null)) {
                return false;
            }
            if (scope.type != 'number' && scope.type != 'autocomplete' && (typeof scope.placeholder !== 'undefined' && scope.placeholder === '') && (typeof scope.ngModel === 'undefined' || scope.ngModel === '' || scope.ngModel === undefined || scope.ngModel === null)) {
                return false;
            }
            if (scope.type == 'autocomplete' && (typeof scope.placeholder !== 'undefined' && scope.placeholder === '') && (typeof scope.ngModel === 'undefined' || scope.displayModel === '' || scope.displayModel === undefined || scope.displayModel === null)) {
                return false;
            }
            return true;
        }

        var setActiveClass = function (scope, element, eventType) {
            scope.active = checkIfActive(scope);
            var el = element.find('#' + scope.textfieldId + '_label');

            if ((typeof scope.active !== 'undefined' && scope.active == true) || (typeof scope.validate !== 'undefined' && scope.validate == true)) {
                if (!el.hasClass('active')) {
                    el.addClass('active');
                }
            } else {
                if (el.hasClass('active')) {
                    el.removeClass('active');
                }
            }
        }

        var updateTextFieldClass = function (scope, element) {
            var elm = $(element).find('#' + scope.textfieldId);
            var elmContainer = $(element).find('#' + scope.textfieldId + '_container');
            var lbl = $(element).find('#' + scope.textfieldId + '_label');
            if (typeof scope.validate !== 'undefined' && scope.validate == true && typeof scope.errorMessage !== 'undefined' && scope.errorMessage) {
                if (!elm.hasClass('invalid')) {
                    elm.addClass('invalid');
                    lbl.attr('data-error', $translate.instant(scope.errorMessage));
                }
            }
            else {
                if (elm.hasClass('invalid')) {
                    elm.removeClass('invalid');
                    lbl.attr('data-error', '');
                }
            }

            if (typeof scope.textAlign !== 'undefined' && scope.textAlign == 'right') {
                if (!elm.hasClass('right-align')) {
                    elm.addClass('right-align');
                }
                if (!lbl.hasClass('right-align')) {
                    lbl.addClass('right-align');
                }
            }
            else {
                if (elm.hasClass('right-align')) {
                    elm.removeClass('right-align');
                }
                if (lbl.hasClass('right-align')) {
                    lbl.removeClass('right-align');
                }
            }

            if (typeof scope.textAlign !== 'undefined' && scope.textAlign == 'left') {
                if (!elm.hasClass('left-align')) {
                    elm.addClass('left-align');
                }
                if (!lbl.hasClass('left-align')) {
                    lbl.addClass('left-align');
                }
            }
            else {
                if (elm.hasClass('left-align')) {
                    elm.removeClass('left-align');
                }
                if (lbl.hasClass('left-align')) {
                    lbl.removeClass('left-align');
                }
            }

            if ((typeof scope.readonly !== 'undefined' && (scope.readonly == true || scope.readonly == 'true')) || (typeof scope.disable !== 'undefined' && (scope.disable === 'true' || scope.disable === true))) {
                elm.prop("readonly", true);
                elm.attr("disabled", "disabled");
                elm.attr('tabindex', -1);
                elmContainer.addClass('readOnlyField');
                if (elm.hasClass('invalid')) {
                    elm.removeClass('invalid');
                    lbl.attr('data-error', '');
                }
            }
            else {
                elm.prop("readonly", false);
                elm.attr("disabled", false);
                elm.removeAttr('tabindex');
                elmContainer.removeClass('readOnlyField');
            }


            if (typeof scope.autocomplete != 'undefiend' && (scope.autocomplete == 'true' || scope.autocomplete == true))// "{{autocomplete?'on':'off'}}")
            {

            }
            else {
                // elm.autocomplete().disable();
            }

            //if (typeof scope.disable !== 'undefined' && scope.disable == true) {
            //    elm.attr("disabled", "disabled")
            //}
            //else {
            //    elm.attr("disabled", false);
            //}


            if (!elm.hasClass('ng-valid')) {
                elm.addClass('ng-pristine ng-valid ng-scope ng-touched');
            }


            if (typeof scope.placeholder !== 'undefined' && scope.placeholder != '') {
                elm.attr("placeholder", scope.placeholder)
            }

            //Optional Field
            var optionalField = $(element).find('#' + scope.textfieldId + '_opField');
            if ((typeof scope.isMandatory !== 'undefined' && (scope.isMandatory == 'false' || scope.isMandatory == false)) && (scope.isRemovable == true || scope.isRemovable == 'true')) {
                var container = $(element).find('#' + scope.textfieldId + '_container');
                if (typeof optionalField == 'undefiend' || (typeof optionalField !== 'undefiend' && optionalField.length == 0)) {
                    container.append(buildOptionalFieldTemplate(scope));
                    optionalField = $(element).find('#' + scope.textfieldId + '_opField');
                    if (typeof optionalField != 'undefined' && optionalField.length > 0) {
                        optionalField.bind("click", scope.hideShowField);
                    }
                }
                if (!container.hasClass('optionalField')) {
                    container.addClass('optionalField');
                }

            }
            else {
                if (typeof optionalField !== 'undefiend' && optionalField.length > 0) {
                    optionalField.remove();
                }
            }

            if (scope.type === 'text' || scope.type === 'number' || scope.type === 'area') {
                if (typeof scope.maxlength != 'undefined') {
                    if (parseInt(scope.maxlength) > -1) {
                        elm.attr('maxlength', parseInt(scope.maxlength));
                    }
                }
            }

            var className = '';
            if (element.length > 0) {
                className = element[0].className;
                className = className.replace('ng-hide', "");
                element.removeClass(className);
            }
            elmContainer.addClass(className);
        }

        var buildOptionalFieldTemplate = function (scope) {
            if ((typeof scope.isMandatory !== 'undefined' && (scope.isMandatory == 'false' || scope.isMandatory === false)) && (scope.isRemovable == true || scope.isRemovable == 'true')) {
                var optionalField = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldOptionalFieldTemplate.html');
                optionalField = optionalField.replace('{{id}}', scope.textfieldId + '_opField');
                return optionalField;
            }
            return '';
        };

        var buildPrefixiconTemplate = function (scope) {
            if (typeof scope.prefixicon !== 'undefined' && scope.prefixicon) {
                var html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldprefixiconTemplate.html');
                html = html.replace('{{id}}', scope.textfieldId + '_prefixicon');
                html = html.replace('{{prefixicon}}', scope.prefixicon);
                return html;
            }
            return '';
        };

        var buildSpinnerTemplate = function (scope) {
            if (!scope.type == 'autocomplete' && typeof scope.loading !== 'undefined' && scope.loading == true) {
                var html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldSpinnerTemplate.html');
                html = html.replace('{{id}}', scope.textfieldId + '_spinner');
                return html;
            }
            return '';
        };

        var buildTextFieldTemplate = function (scope) {
            var html = '';
            if (scope.type === 'area') {
                html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextareaElementTemplate.html');
            } else {
                html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldElementTemplate.html');
            }
            if (scope.type === 'password') {
                html = html.replace('type="text"', 'type="password"');
            }
            html = html.replace('{{id}}', scope.textfieldId);
            return html;
        };

        var buildInfoIconTemplate = function (scope) {
            var html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldinfoiconTemplate.html');
            //if (scope.showwarning || scope.showinfo) {
            //    html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldinfoiconTemplate.html');
            //}
            return html;
        };

        //var buildWarningIconTemplate = function (scope) {
        //    var html = '';
        //    if (scope.showwarning || scope.showinfo) {
        //        html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldwarningiconTemplate.html');
        //    }
        //    return html;
        //};

        var buildLabelTemplate = function (scope) {
            var html = '';
            //if (typeof scope.label !== 'undefined' && scope.label.trim() != '') {
            html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldLabelTemplate.html');
            html = html.replace('{{id}}', scope.textfieldId + '_label');
            html = html.replace('{{textfieldId}}', scope.textfieldId);
            html = html.replace('{{label}}', typeof scope.label === 'undefined' ? '' : (scope.label + formWidgetUtils.getMandatoryFieldHtml(scope.isMandatory, scope)));
            if (!scope.label) {
                html = html.replace('class=""', 'class="empty-label"');
            }

            //}
            return html;
        };

        var buildTemplate = function (scope, element, onChangeCallback, attrs, ngModelCtrl) {
            var container = $('<div/>', { id: scope.textfieldId + '_container' });// document.createElement('div');

            var css = '';
            if ((scope.isMandatory === false || scope.isMandatory === 'false') && scope.isRemovable === true) {
                css += ' optionalField';
            }
            if (scope.showinfo || scope.showwarning) {
                css += ' field-has-icon';
                //if (scope.showinfo && scope.showwarning) {
                //    css += ' field-has-two-icons';
                //}
            }
            if (scope.readonly === 'true' || scope.disable === 'true') {
                css += ' readOnlyField';
            }
            //if (!scope.customoption) {
            //    css += ' defaultSelected';
            //}
            //if (scope.validate === true && scope.errorMessage) {
            //    css += ' invalid';
            //}
            var className = '';
            if (element.length > 0) {
                className = element[0].className;
                element.removeClass(className);
            }
            container.addClass(className + ' input-field ' + css);


            container.append('<div class="elements-include">' + buildOptionalFieldTemplate(scope) + buildPrefixiconTemplate(scope) + buildSpinnerTemplate(scope) + buildTextFieldTemplate(scope) + buildLabelTemplate(scope) + '</div>');
            container.append($compile(buildInfoIconTemplate(scope))(scope));
            //if (scope.showinfo || scope.showwarning) {
            //    container.append($compile(buildInfoIconTemplate(scope))(scope));
            //}

            element.html(container);
            var elm = $(element).find('#' + scope.textfieldId);
            elm.on("change", function (el) {
                //console.log(el);
                onChangeCallback(el);
            });
            elm.on("blur", scope.ngBlur);

            if (scope.type != undefined && scope.type == 'number') {
                // elm.attr('type', 'number');

                if (scope.min != undefined && scope.min != null) {
                    if ($.isNumeric(scope.min)) {
                        elm.attr('min', scope.min);
                    }
                }
                if (scope.max != undefined && scope.max != null) {
                    if ($.isNumeric(scope.max)) {
                        elm.attr('max', scope.max);
                    }
                }

                elm.bind('keypress', scope.ngKeyPress);



            } else if (scope.type != undefined && scope.type == 'autocomplete') {
                elm.on("focus", scope.ngFocus);

                if (attrs.onKeyPress || attrs.onEnter) {
                    elm.bind('keypress', debounce(scope.ngKeyPress, 250));
                }
            } else if (scope.type != undefined && (scope.type == 'area' || scope.type == 'text')) {

                // Need to add debounce for updating the ngModel
                if (attrs.onKeyPress || attrs.onEnter) {
                    elm.bind('keypress', debounce(scope.ngKeyPress, 250));
                }
                //if (attrs.onKeyUp) {
                //    elm.bind('keyup', debounce(scope.ngKeyUp, 250));
                //}
                if (attrs.onKeyDown) {
                    elm.bind('keydown', debounce(scope.ngKeyDown, 250));
                }
                if (attrs.onFocus) {
                    elm.bind('focus', scope.ngFocus);
                }
                // Need to optimize this line, post finding of better approach. It is required for filtering ng-repeat
                elm.bind('keyup', scope.ngKeyUp);
            }


            var optionalField = $(element).find('#' + scope.textfieldId + '_opField > i');
            if (typeof optionalField != 'undefined' && optionalField.length > 0) {
                optionalField.bind("click", scope.hideShowField);

            }
            updateTextFieldClass(scope, element);
            setActiveClass(scope, element);

            if (angular.isDefined(scope.charactercounter) && scope.charactercounter == 'true') {
                elm.attr('length', scope.maxlength);
                elm.characterCounter();
            }

        };

        var updateTextValue = function (scope, element, ngModelCtrl) {
            var elm = element.find('#' + scope.textfieldId);
             if (!scope.typing) {
                    elm.val(modal.getModal(scope, ngModelCtrl));
                }
                if (scope.showTitle) {
                    elm.attr('title', modal.getModal(scope, ngModelCtrl));
                }
                if (modal.getModal(scope, ngModelCtrl) === '') {
                    elm.addClass('ng-empty').removeClass('ng-not-empty');
                } else {
                    elm.removeClass('ng-empty').addClass('ng-not-empty');
                }

                /* Adding class truncate to input*/
            if (!elm.hasClass('materialize-textarea') && scope.readonly === 'true' || scope.disable === 'true') {
                    elm.addClass('truncate');
                } else {
                    elm.removeClass('truncate')
                };
            }
        
      var modal = {
            getModal: function (scope, ngModelCtrl) {
                return typeof scope.ngModel == "function" ? ngModelCtrl.$modelValue : scope.ngModel;
            },
            setModal: function (scope, value, ngModelCtrl) {
                if (typeof scope.ngModel == "function") {
                    ngModelCtrl.$setViewValue(value)
                    ngModelCtrl.$render()
                } else {
                    scope.ngModel = value;
                }
            }
        };

        var updateDateValue = function (scope, element) {
            var elm = element.find('#' + scope.textfieldId);
            elm.val(scope.dateModel);
            elm.attr('title', scope.dateModel);
        }

        var updateAutocompleteValue = function (scope, element) {
            var elm = element.find('#' + scope.textfieldId);
            elm.val(scope.displayModel);
            elm.attr('title', scope.displayModel);
        }


        var checkRule = function (scope, element) {
            var rules = scope.$eval(scope.rules);
            var isFoundInvalid = false;

            for (var i = 0; i < rules.length; i++) {
                if (typeof rules[i] == 'object') {
                    if (eval((rules[i].rule).replace(/this/g, scope.type == 'autocomplete' ? 'scope.displayModel' : 'scope.ngModel'))) {
                        scope.validate = true;
                        scope.errorMessage = rules[i].error;
                        scope.errorModel = rules[i].error;
                        break;
                    } else {
                        if (scope.type === 'date') {
                            angular.element('#' + scope.textfieldId).removeClass('invalid');
                        }
                    }
                }
                else {
                    if (!isFoundInvalid) {
                        if (typeof scope.ngModel != 'object')
                            var data = scope.ngModel;
                        else
                            var data = scope.displayModel;

                        RuleEngine.isValid(rules[i], function (e) {
                            scope.validate = true;
                            scope.errorMessage = e.errorData.error;
                            scope.errorModel = rules[i].error;
                            isFoundInvalid = true;
                        }, data);
                    }
                    else {
                        break;
                    }
                }
            }
        }

        var validateRules = function (scope, ngModelCtrl, element, attrs) {
            if (typeof scope.readonly !== 'undefined' && scope.readonly == 'true') {
                scope.isActive = true;
                return;
            }

            if ((scope.type === 'text' || scope.type === 'area') && angular.isString(scope.ngModel)) {
                if (attrs.noTrim == 'true') {
                    //scope.ngModel = scope.ngModel;
                } else {
                    //scope.ngModel = scope.ngModel.trim();
                    modal.setModal(scope, modal.getModal(scope, ngModelCtrl).trim(), ngModelCtrl);
                }
                //ngModelCtrl.$setViewValue(scope.ngModel);
            }

            if ((scope.type == 'autocomplete' && scope.$eval(scope.isMandatory) && !scope.clienterror && (typeof scope.ngModel === 'undefined' || scope.displayModel == '' || scope.displayModel == null || scope.displayModel == undefined || scope.ngModel.id === 0)) || (scope.type != 'autocomplete' && scope.$eval(scope.isMandatory) && !scope.clienterror && (scope.ngModel + '' == '' || scope.ngModel == null || scope.ngModel == undefined))) {
                scope.validate = true;
                scope.errorMessage = $translate.instant("enterValueErrMsg");
                scope.errorModel = $translate.instant("enterValueErrMsg");

                return;
            }

            if (scope.clienterror) {
                scope.validate = true;
                scope.errorMessage = $translate.instant("clientErrMsg");
                scope.errorModel = $translate.instant("clientErrMsg");

                return;
            }

            if (scope.type == 'number') {
                if (!scope.$eval(scope.isMandatory) && isNaN(scope.ngModel)) {
                    angular.element('#' + scope.textfieldId).val('');
                    return;
                }
                if (!scope.$eval(scope.isMandatory) && scope.ngModel === null) {
                    if (scope.rules) {
                        checkRule(scope, element);
                    }
                    return;
                }
                //if (scope.$eval(scope.isMandatory) && isNaN(scope.ngModel) || (!scope.$eval(scope.isMandatory) && number == undefined)) {
                //    scope.validate = true;
                //    scope.errorMessage = $translate.instant('enterNumberErrMsg');
                //    scope.errorModel = $translate.instant('enterNumberErrMsg');
                //    return;
                //}

            }

            // CON-5898
            if (scope.type == 'date' && !scope.rules) {
                $('#' + scope.textfieldId).removeClass('invalid');
            }

            scope.validate = false;
            scope.errorMessage = null;
            scope.errorModel = null;

            if (scope.rules) {
                checkRule(scope, element);
            }
        };

        var debounce = function (func, wait, immediate) {
            var timeout;
            return function () {
                var context = this, args = arguments;
                var later = function () {
                    timeout = null;
                    if (!immediate) func.apply(context, args);
                };
                var callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) func.apply(context, args);
            };
        };

        var updateDateClass = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            if (scope.type == 'date') {
                if (!el.hasClass('datepicker')) {
                    el.addClass('datepicker');
                }
            }


        }
        var assignDateEvents = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            el.bind('keydown', scope.ngKeyDown);
            el.bind('keypress', scope.ngKeyPress);
            el.bind('keyup', scope.ngKeyUp);

        }

        var assignAutocompleteEvents = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            //el.bind('keydown', scope.ngKeyDown);
            //el.bind('keypress', scope.ngKeyPress);
            el.bind('keyup', debounce(scope.ngKeyUp, 250));
            el.on('input', debounce(scope.search, 250));

        }

        /*
          * This function is required to generate value attribute 
          * and useful when up/down key is pressed
          */
        var getFormattedData = function (data, scope) {
            if (!data || data.length == 0) {
                return [];
            }
            for (var i = 0; i < data.length; i++) {
                var str = scope.ndisplayFormat;
                try {
                    var parentSplit = str.split('{');
                    for (var j = 0; j < parentSplit.length; j++) {
                        var childSplit = parentSplit[j].split('}');
                        for (var k = 0; k < childSplit.length; k++) {
                            if (childSplit[k].indexOf('.') > 0 || data[i].hasOwnProperty(childSplit[k])) {
                                str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), eval('data[i].' + childSplit[k]));
                            }
                        }
                    }
                }
                catch (e) { }
                data[i].value = str.trim();
            }
            return data;
        };
        /*
            *  Convert timestamp to UTC timestamp 
            */
        var toUTC = function (date) {
            return Date.UTC(
                date.getFullYear()
                , date.getMonth()
                , date.getDate()
                , date.getHours()
                , date.getMinutes()
                , date.getSeconds()
                , date.getMilliseconds()
            );
        };

        var getGridFormatToTimestamp = function (scope, stringdate) {
            var tmpModel = (stringdate + '').replace(new RegExp("/", "ig"), '');
            // tmpModel = tmpModel.split('+');
            // tmpModel = eval('new ' + (tmpModel.length > 1 ? ('Date("' + tmpModel.join('+') + '").getTime()') : tmpModel[0]));
            //if (scope.applyClientZone) {
            //    return moment(tmpModel).parseZone().format(scope.dateFormat);
            //}
            //else {
            return moment(tmpModel).format(scope.dateFormat);
            //}
        }

        var updateNgModel = function (scope, timestamp) {
            if (scope.timePicker === 'true') {
                scope.timePicker = true;
            }
            scope.dateModifiedbyUser = true;
            if (scope.timePicker == true) {
                if (scope.convertIntoUTC || timeZoneOffset != 20) {
                    // that means the date will be saved in UTC Format
                    scope.ngModel = moment(timestamp * 1000).add((timeZoneOffset * -60), 'minute').format('YYYY-MM-DDTHH:mm:ss');  //'\/Date(' + ((timestamp - (moment().zone() * 60)) * 1000) + ')\/';
                } else {
                    //This case is applied for Sourcing. because of the converstion happed before saving.
                    scope.ngModel = '\/Date(' + (timestamp * 1000) + ')\/';
                }
            }
            else {
                // hooking a fixed time to fix the flipping issues.
                // if (scope.convertIntoUTC) {
                //scope.ngModel = moment(timestamp * 1000).format('YYYY-MM-DD') + 'T12:00:00';//'\/Date(' + (((timestamp- (moment().zone() * 60)) * 1000) + 43200000) + ')\/';
                //}else
                //{
                //    scope.ngModel = '\/Date(' + ((timestamp * 1000) + 43200000) + ')\/';
                //}

                if (timeZoneOffset != 20) {
                    scope.ngModel = moment(timestamp * 1000).format('YYYY-MM-DD') + 'T12:00:00';//'\/Date(' + (((timestamp- (moment().zone() * 60)) * 1000) + 43200000) + ')\/';
                } else {
                    scope.ngModel = '\/Date(' + ((timestamp * 1000) + 43200000) + ')\/';
                }
            }
        }


        var createDatePicker = function (scope, element, ngModelCtrl) {
            setTimeout(function () {
                var picker = element.find('#' + scope.textfieldId);
                var callApplyFuncFlag = true;
                function setDateFormate(datePar) {
                    updateNgModel(scope, datePar.unix());
                    $timeout(function () {
                        // if (scope.doesContainDateString) {

                        //} else {
                        //    if (scope.isValueDateObj) {
                        //        var tempDate = datePar.format();
                        //        scope.ngModel = new Date(tempDate);
                        //    } else {
                        //        scope.ngModel = (datePar.unix() * 1000);
                        //    }
                        //}
                        // ngModelCtrl.$setViewValue(scope.ngModel);
                        scope.isDateBlank = false;
                        if (angular.isFunction(scope.onDateChange)) {
                            scope.onDateChange({ $event: { date: scope.ngModel }});
                        }

                    })
                }
                var minDate = angular.isDefined(scope.min) ? scope.doesContainDateString ? getGridFormatToTimestamp(scope.min) : moment(parseInt(scope.min)).format(scope.dateFormat) : '';
                var maxDate = angular.isDefined(scope.max) ? scope.doesContainDateString ? getGridFormatToTimestamp(scope.max) : moment(parseInt(scope.max)).format(scope.dateFormat) : '';

                var timePicker24Hour = angular.isDefined(scope.timePicker12Hour) ? false : true;
                var timePickerIncrement = angular.isDefined(scope.minuteIncrement) ? scope.$eval(scope.minuteIncrement) : 1;

                var pickerObj = picker.daterangepicker({
                    "singleDatePicker": true,
                    "showDropdowns": true,
                    "timePicker": scope.datetimePicker,
                    "timePicker24Hour": timePicker24Hour,
                    "timePickerIncrement": timePickerIncrement,
                    "autoApply": false,
                    "opens": "right",
                    "drops": "down",
                    "minDate": minDate,
                    "maxDate": maxDate,
                    "autoUpdateInput": false,
                    "locale": {
                        "format": scope.dateFormat,
                        'cancelLabel': $translate.instant('cancelBtn'),
                        'applyLabel': $translate.instant('applyBtn')
                    }
                }, function (start, end, label) {
                    callApplyFuncFlag = false;
                    setDateFormate(start);
                }).bind("show.daterangepicker", function () { // callback on after open datepicker
                    var pos = pickerObj.container.offset(),
                        wHeight = window.innerHeight,
                        srTop = $(window).scrollTop();
                    if ((pos.top + pickerObj.container.outerHeight() - srTop) > wHeight) { // if datepicker goes down then push it up
                        pickerObj.drops = "up";
                        pickerObj.move();
                    } else if (pos.top < srTop) { // else if datepicker goes up then push it down
                        pickerObj.drops = "down";
                        pickerObj.move();
                    }
                }).data("daterangepicker");

                picker.on('apply.daterangepicker', function (ev, pickerObj) {
                    if (callApplyFuncFlag) {
                        setDateFormate(pickerObj.startDate)
                    }
                    callApplyFuncFlag = true;
                    pickerObj.autoUpdateInput = true;
                    pickerObj.updateElement();
                    pickerObj.autoUpdateInput = false;
                });

                if (scope.isDateBlank) {
                    picker.val('');
                    scope.ngModel = "";
                    // WS-2072
                    //ngModelCtrl.$setViewValue('');

                };
            });
        }

        var getDisplayData = function (data, scope) {
            if (data == undefined || data == null || data == '') {
                return '';
            }
            var str = scope.ndisplayFormat, evalResult, evaluatedKeysCounter = 0, nullKeysCounter = 0;
            try {
                var parentSplit = str.split('{');
                for (var j = 0; j < parentSplit.length; j++) {
                    var childSplit = parentSplit[j].split('}');
                    for (var k = 0; k < childSplit.length; k++) {
                        if (childSplit[k].indexOf('.') > 0 || data.hasOwnProperty(childSplit[k])) {
                            evalResult = eval('data.' + childSplit[k]);
                            evaluatedKeysCounter++;
                            if (evalResult == undefined || evalResult == null || (evalResult + '').trim().length == 0) {
                                evalResult = '';
                                nullKeysCounter++;
                            }
                            str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), evalResult);
                        }
                    }
                }
                if (evaluatedKeysCounter == nullKeysCounter) {
                    str = '';
                }
            }
            catch (e) { }
            return str === undefined || str === null || str === 'undefined' || str === 'null' ? '' : str;
        }

        function debounce(func, wait, immediate) {
            var timeout;
            return function () {
                var context = this, args = arguments;
                var later = function () {
                    timeout = null;
                    if (!immediate) func.apply(context, args);
                };
                var callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) func.apply(context, args);
            };
        };  


        var service = {
            buildTemplate: buildTemplate,
            setActiveClass: setActiveClass,
            updateTextFieldClass: updateTextFieldClass,
            checkIfActive: checkIfActive,
            validateRules: validateRules,
            checkRule: checkRule,
            updateTextValue: updateTextValue,
            debounce: debounce,
            updateDateClass: updateDateClass,
            assignDateEvents: assignDateEvents,
            getFormattedData: getFormattedData,
            toUTC: toUTC,
            getGridFormatToTimestamp: getGridFormatToTimestamp,
            createDatePicker: createDatePicker,
            updateDateValue: updateDateValue,
            updateAutocompleteValue: updateAutocompleteValue,
            getDisplayData: getDisplayData,
            assignAutocompleteEvents: assignAutocompleteEvents,
            modal: modal
        };

        return service;
    }

    angular.module('SMART2').factory('smartTextField3Service', ['ScrollTo', 'RuleEngine', '$templateCache', '$compile', '$translate', '$timeout', 'formWidgetUtils', smartTextField3ServiceFunc])
    function smartTextField3ServiceFunc(ScrollTo, RuleEngine, $templateCache, $compile, $translate, $timeout, formWidgetUtils) {
        var _selectedDate, _previousDate;
        var getSelectedDate = function () {
            return _selectedDate;
        };
        var setSelectedDate = function (date) {
            _selectedDate = date;
        };
        var getPreviousDate = function () {
            return _previousDate;
        };
        var setPreviousDate = function (date) {
            _previousDate = date;
        };
        var checkIfActive = function (scope, ngModelCtrl) {
            if (typeof scope.readonly !== 'undefined' && scope.readonly == 'true') {
                return true;
        }
            if (scope.type == 'number' && scope.ngModel !== 0 + '' && (typeof scope.placeholder !== 'undefined' && scope.placeholder === '') && (typeof scope.ngModel === 'undefined' || scope.ngModel === '' || isNaN(scope.ngModel) || scope.ngModel === undefined || scope.ngModel === null)) {
                return false;
        }
            if (scope.type != 'number' && scope.type != 'autocomplete' && (typeof scope.placeholder !== 'undefined' && scope.placeholder === '') && (typeof scope.ngModel === 'undefined' || scope.ngModel === '' || scope.ngModel === undefined || scope.ngModel === null)) {
                return false;
        }
            if (scope.type == 'autocomplete' && (typeof scope.placeholder !== 'undefined' && scope.placeholder === '') && (typeof scope.ngModel === 'undefined' || scope.displayModel === '' || scope.displayModel === undefined || scope.displayModel === null)) {
                return false;
        }
            if (typeof scope.ngModel == "function" && ngModelCtrl && (ngModelCtrl.$modelValue == undefined || ngModelCtrl.$modelValue === '')) {
                return false;
            }
            return true;
        }

        var setActiveClass = function (scope, element, ngModelCtrl) {
            scope.active = checkIfActive(scope, ngModelCtrl);
            var el = element.find('#' + scope.textfieldId + '_label');

            if ((typeof scope.active !== 'undefined' && scope.active == true) || (typeof scope.validate !== 'undefined' && scope.validate == true)) {
                if (!el.hasClass('active')) {
                    el.addClass('active');
            }
            } else {
                if (el.hasClass('active')) {
                    el.removeClass('active');
            }
        }
        }

        var updateTextFieldClass = function (scope, element) {
            var elm = $(element).find('#' + scope.textfieldId);
            var elmContainer = $(element).find('#' + scope.textfieldId + '_container');
            var lbl = $(element).find('#' + scope.textfieldId + '_label');
            if (typeof scope.validate !== 'undefined' && scope.validate == true && typeof scope.errorMessage !== 'undefined' && scope.errorMessage) {
                if (!elm.hasClass('invalid')) {
                    elm.addClass('invalid');
                    lbl.attr('data-error', $translate.instant(scope.errorMessage));
            }
            }
            else {
                if (elm.hasClass('invalid')) {
                    elm.removeClass('invalid');
                    lbl.attr('data-error', '');
            }
        }

            if (typeof scope.textAlign !== 'undefined' && scope.textAlign == 'right') {
                if (!elm.hasClass('right-align')) {
                    elm.addClass('right-align');
            }
            }
            else {
                if (elm.hasClass('right-align')) {
                    elm.removeClass('right-align');
            }
        }

            if (typeof scope.textAlign !== 'undefined' && scope.textAlign == 'left') {
                if (!elm.hasClass('left-align')) {
                    elm.addClass('left-align');
            }
            }
            else {
                if (elm.hasClass('left-align')) {
                    elm.removeClass('left-align');
            }
        }

            if ((typeof scope.readonly !== 'undefined' && (scope.readonly == true || scope.readonly == 'true')) || (typeof scope.disable !== 'undefined' && (scope.disable === 'true' || scope.disable === true))) {
                elm.prop("readonly", true);
                elm.attr("disabled", "disabled");
                elm.attr('tabindex', -1);
                elmContainer.addClass('readOnlyField');
                if (elm.hasClass('invalid')) {
                    elm.removeClass('invalid');
                    lbl.attr('data-error', '');
            }
            }
            else {
                elm.prop("readonly", false);
                elm.attr("disabled", false);
                elm.removeAttr('tabindex');
                elmContainer.removeClass('readOnlyField');
        }


            if (typeof scope.autocomplete != 'undefiend' && (scope.autocomplete == 'true' || scope.autocomplete == true))// "{{autocomplete?'on':'off'}}")
            {

            }
            else {
                // elm.autocomplete().disable();
        }

            //if (typeof scope.disable !== 'undefined' && scope.disable == true) {
            //    elm.attr("disabled", "disabled")
            //}
            //else {
            //    elm.attr("disabled", false);
            //}


            if (!elm.hasClass('ng-valid')) {
                elm.addClass('ng-pristine ng-valid ng-scope ng-touched');
        }


            if (typeof scope.placeholder !== 'undefined' && scope.placeholder != '') {
                elm.attr("placeholder", scope.placeholder)
        }

            //Optional Field
            var optionalField = $(element).find('#' + scope.textfieldId + '_opField');
            if ((typeof scope.isMandatory !== 'undefined' && (scope.isMandatory == 'false' || scope.isMandatory == false)) && (scope.isRemovable == true || scope.isRemovable == 'true')) {
                var container = $(element).find('#' + scope.textfieldId + '_container');
                if (typeof optionalField == 'undefiend' || (typeof optionalField !== 'undefiend' && optionalField.length == 0)) {
                    container.append(buildOptionalFieldTemplate(scope));
                    optionalField = $(element).find('#' + scope.textfieldId + '_opField');
                    if (typeof optionalField != 'undefined' && optionalField.length > 0) {
                        optionalField.bind("click", scope.hideShowField);
                }
            }
                if (!container.hasClass('optionalField')) {
                    container.addClass('optionalField');
            }

            }
            else {
                if (typeof optionalField !== 'undefiend' && optionalField.length > 0) {
                    optionalField.remove();
            }
        }

            if (scope.type === 'text' || scope.type === 'number' || scope.type === 'area') {
                if (typeof scope.maxlength != 'undefined') {
                    if (parseInt(scope.maxlength) > -1) {
                        elm.attr('maxlength', parseInt(scope.maxlength));
                }
            }
        }

            var className = '';
            if (element.length > 0) {
                className = element[0].className;
                className = className.replace('ng-hide', "");
                element.removeClass(className);
        }
            elmContainer.addClass(className);
        }

        var buildOptionalFieldTemplate = function (scope) {
            if ((typeof scope.isMandatory !== 'undefined' && (scope.isMandatory == 'false' || scope.isMandatory === false)) && (scope.isRemovable == true || scope.isRemovable == 'true')) {
                var optionalField = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldOptionalFieldTemplate.html');
                optionalField = optionalField.replace('{{id}}', scope.textfieldId + '_opField');
                return optionalField;
        }
            return '';
        };

        var buildPrefixiconTemplate = function (scope) {
            if (typeof scope.prefixicon !== 'undefined' && scope.prefixicon) {
                var html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldprefixiconTemplate.html');
                html = html.replace('{{id}}', scope.textfieldId + '_prefixicon');
                html = html.replace('{{prefixicon}}', scope.prefixicon);
                return html;
        }
            return '';
        };

        var buildSpinnerTemplate = function (scope) {
            if (!scope.type == 'autocomplete' && typeof scope.loading !== 'undefined' && scope.loading == true) {
                var html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldSpinnerTemplate.html');
                html = html.replace('{{id}}', scope.textfieldId + '_spinner');
                return html;
        }
            return '';
        };

        var buildTextFieldTemplate = function (scope) {
            var html = '';
            if (scope.type === 'area' && scope.readonly === true) {
                html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextareaReadonlyElementTemplate.html');
            } else if (scope.type === 'area') {
                html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextareaElementTemplate.html');
            } else {
                html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldElementTemplate.html');
        }
            if (scope.type === 'password') {
                html = html.replace('type="text"', 'type="password"');
        }
            html = html.replace('{{id}}', scope.textfieldId);
            html = html.replace('{{showHideReadMoreContent}}', 'showHideReadMoreContent' + scope.textfieldId);
            return html;
        };

        var buildInfoIconTemplate = function (scope) {
            var html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldinfoiconTemplate.html');
            //if (scope.showwarning || scope.showinfo) {
            //    html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldinfoiconTemplate.html');
            //}
            return html;
        };

            //var buildWarningIconTemplate = function (scope) {
            //    var html = '';
            //    if (scope.showwarning || scope.showinfo) {
            //        html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldwarningiconTemplate.html');
            //    }
            //    return html;
            //};

        var buildLabelTemplate = function (scope) {
            var html = '';
            //if (typeof scope.label !== 'undefined' && scope.label.trim() != '') {
            html = $templateCache.get('shared/directives/uiElements/smartTextfield/smartTextfieldLabelTemplate.html');
            html = html.replace('{{id}}', scope.textfieldId + '_label');
            html = html.replace('{{textfieldId}}', scope.textfieldId);
            html = html.replace('{{label}}', typeof scope.label === 'undefined' ? '' : (scope.label + formWidgetUtils.getMandatoryFieldHtml(scope.isMandatory, scope)));
            if (!scope.label) {
                html = html.replace('class=""', 'class="empty-label"');
        }

            //}
            return html;
        };

        var buildTemplate = function (scope, element, onChangeCallback, attrs, ngModelCtrl) {
            var container = $('<div/>', { id: scope.textfieldId + '_container' });// document.createElement('div');

            var css = '';
            if ((scope.isMandatory === false || scope.isMandatory === 'false') && scope.isRemovable === true) {
                css += ' optionalField';
        }
            if (scope.showinfo || scope.showwarning) {
                css += ' field-has-icon';
                //if (scope.showinfo && scope.showwarning) {
                //    css += ' field-has-two-icons';
                //}
        }
            if (scope.readonly === 'true' || scope.disable === 'true') {
                css += ' readOnlyField';
        }
            //if (!scope.customoption) {
            //    css += ' defaultSelected';
            //}
            //if (scope.validate === true && scope.errorMessage) {
            //    css += ' invalid';
            //}
            var className = '';
            if (element.length > 0) {
                className = element[0].className;
                element.removeClass(className);
            }
            container.addClass(className + ' input-field ' + css);


            container.append('<div class="elements-include">' + buildOptionalFieldTemplate(scope) + buildPrefixiconTemplate(scope) + buildSpinnerTemplate(scope) + buildTextFieldTemplate(scope) + buildLabelTemplate(scope) + '</div>');
            container.append($compile(buildInfoIconTemplate(scope))(scope));
            //if (scope.showinfo || scope.showwarning) {
            //    container.append($compile(buildInfoIconTemplate(scope))(scope));
            //}

            element.html(container);
            var elm = $(element).find('#' + scope.textfieldId);
            elm.on("change", function (el) {
                //console.log(el);
                onChangeCallback(el);
            });
            elm.on("blur", scope.ngBlur);

            if (scope.type != undefined && scope.type == 'number') {
                // elm.attr('type', 'number');

                if (scope.min != undefined && scope.min != null) {
                    if ($.isNumeric(scope.min)) {
                        elm.attr('min', scope.min);
                }
            }
                if (scope.max != undefined && scope.max != null) {
                    if ($.isNumeric(scope.max)) {
                        elm.attr('max', scope.max);
                }
            }

                elm.bind('keypress', scope.ngKeyPress);



            } else if (scope.type != undefined && scope.type == 'autocomplete') {
                elm.on("focus", scope.ngFocus);

                if (attrs.onKeyPress || attrs.onEnter) {
                    elm.bind('keypress', debounce(scope.ngKeyPress, 250));
            }
            } else if (scope.type != undefined && (scope.type == 'area' || scope.type == 'text')) {

                // Need to add debounce for updating the ngModel
                if (attrs.onKeyPress || attrs.onEnter) {
                    elm.bind('keypress', debounce(scope.ngKeyPress, 250));
            }
                //if (attrs.onKeyUp) {
                //    elm.bind('keyup', debounce(scope.ngKeyUp, 250));
                //}
                if (attrs.onKeyDown) {
                    elm.bind('keydown', debounce(scope.ngKeyDown, 250));
            }
                if (attrs.onFocus) {
                    elm.bind('focus', scope.ngFocus);
            }
                // Need to optimize this line, post finding of better approach. It is required for filtering ng-repeat
                elm.bind('keyup', scope.ngKeyUp);
            }


            var optionalField = $(element).find('#' + scope.textfieldId + '_opField > i');
            if (typeof optionalField != 'undefined' && optionalField.length > 0) {
                optionalField.bind("click", scope.hideShowField);

        }
            updateTextFieldClass(scope, element);
            setActiveClass(scope, element, ngModelCtrl);

            if (angular.isDefined(scope.charactercounter) && scope.charactercounter == 'true') {
                elm.attr('length', scope.maxlength);
                elm.characterCounter();
        }

        };

        var updateTextValue = function (scope, element, ngModelCtrl) {
            var elm = element.find('#' + scope.textfieldId),
                addClassElem = element.find('#showHideReadMoreContent' + scope.textfieldId),
                showLessContent = element.find('.readOnlyshowLess'),
                showMoreContent = element.find('.readOnlyShowMore');
                showLessContent.hide();
                showMoreContent.hide();
            if (!addClassElem.hasClass('areareadOnlyContentPara2')) {
                if (modal.getModal(scope, ngModelCtrl) && modal.getModal(scope, ngModelCtrl).length < 150) {
                    showLessContent.hide();
                    showMoreContent.hide();
                } else if (modal.getModal(scope, ngModelCtrl) && modal.getModal(scope, ngModelCtrl).length > 150) {
                    addClassElem.addClass('ellipsis areareadOnlyContentPara1');
                    showLessContent.hide();
                    showMoreContent.show();
            }
        }
         
            if (scope.type === 'area' && scope.readonly === true) {
                elm.text(modal.getModal(scope, ngModelCtrl));
                $(showLessContent).off('click').on('click', function () {
                    $(this).hide();
                    $(showMoreContent).show();
                    $(addClassElem).removeClass('areareadOnlyContentPara2');
                    $(addClassElem).addClass('ellipsis areareadOnlyContentPara1');
                });
                $(showMoreContent).off('click').on('click', function () {
                    $(this).hide();
                    $(showLessContent).show();
                    $(addClassElem).removeClass('ellipsis areareadOnlyContentPara1');
                    $(addClassElem).addClass('areareadOnlyContentPara2');
                });
            }
            else {
                if (typeof scope.ngModel == "function") {
                    $timeout(function () {
                        elm = element.find('#' + scope.textfieldId);
                        var gm = modal.getModal(scope, ngModelCtrl);
                        if (elm.val() !== gm) {
                            elm.val(gm);
                        }
                        if (scope.showTitle) {
                            elm.attr('title', gm);
                        }
                        if (gm === '' || typeof gm == 'undefined') {
                            elm.addClass('ng-empty').removeClass('ng-not-empty');
                            elm.attr('title', '');
                        } else {
                             angular.element('#' + scope.textfieldId+"_label").addClass("active");
                            elm.removeClass('ng-empty').addClass('ng-not-empty');
                        }
                    });
                } else if (scope.type == 'number' && scope.infinite) {
                    var modelVal = modal.getModal(scope, ngModelCtrl)
                    if (typeof elm.autoNumeric == 'function') {
                        if (elm.autoNumeric('get') != modelVal && !scope.infinite) {
                            elm.autoNumeric('set', modelVal);
                        }
                        if (scope.showTitle) {
                            elm.attr('title', elm.autoNumeric('get'));
                        }
                    }
                    else {
                        if (elm.val() !== modelVal) {
                            elm.val(modelVal);
                        }
                        if (scope.showTitle) {
                            elm.attr('title', modelVal);
                        }
                    }
                   
                    if (modelVal === null && scope.placeholder === "") {
                        if ((scope.infinite == "true" && scope.type == "number") && (modelVal === 'undefined' || modelVal === null)) {
                            angular.element('#' + scope.textfieldId).removeClass('ng-empty').addClass('ng-not-empty');
                            angular.element('#' + scope.textfieldId + '_label').css({ "font-size": "12px", "bottom": "37px" });
                        } else {
                            elm.addClass('ng-empty').removeClass('ng-not-empty');
                        }
                    }
                    else if (modelVal === '∞') {
                        angular.element('#' + scope.textfieldId).removeClass('ng-empty').addClass('ng-not-empty');
                        angular.element('#' + scope.textfieldId + '_label').css({ "font-size": "12px", "bottom": "37px" });
                    }
                    else {
                        elm.removeClass('ng-empty').addClass('ng-not-empty');
                    }
                }
                else {
                    if (elm.val() !== modal.getModal(scope, ngModelCtrl)) {
                        if (scope.typing) {
                            smartTextFieldService.modal.setModal(scope, elm.val(), ngModelCtrl);
                        } else {
                            elm.val(modal.getModal(scope, ngModelCtrl));
                        }

                    }
                    if (scope.showTitle) {
                        elm.attr('title', modal.getModal(scope, ngModelCtrl));
                    }
                    if (modal.getModal(scope, ngModelCtrl) === '') {
                        elm.addClass('ng-empty').removeClass('ng-not-empty');
                    } else {
                        elm.removeClass('ng-empty').addClass('ng-not-empty');
                    }
                }

                /* Adding class truncate to input*/
                if (!elm.hasClass('materialize-textarea') && (scope.readonly === 'true' || scope.disable === 'true')) {
                    elm.addClass('truncate');
                } else {
                    elm.removeClass('truncate')
            };
        }
            if (scope.type === 'area') {
                    $timeout(function(){
                         angular.element('#' + scope.textfieldId).trigger('autoresize');
                    });
            }
        }

        var modal = {
                getModal: function (scope, ngModelCtrl) {
                return typeof scope.ngModel == "function" ? ngModelCtrl.$modelValue : scope.ngModel;
        },
                setModal: function (scope, value, ngModelCtrl) {

                if (typeof scope.ngModel == "function") {
                    ngModelCtrl.$setViewValue(value)
                    ngModelCtrl.$render()
                } else {
                    scope.ngModel = value;
                }
        }
        };

        var updateDateValue = function (scope, element) {
            var elm = element.find('#' + scope.textfieldId);
            elm.val(scope.dateModel);
            elm.attr('title', scope.dateModel);
        }

        var updateAutocompleteValue = function (scope, element) {
            var elm = element.find('#' + scope.textfieldId);
            elm.val(scope.displayModel);
            elm.attr('title', scope.displayModel);
        }


        var checkRule = function (scope, element) {
            var rules = scope.$eval(scope.rules);
            var isFoundInvalid = false;

            for (var i = 0; i < rules.length; i++) {
                if (typeof rules[i] == 'object') {
                    if (eval((rules[i].rule).replace(/this/g, scope.type == 'autocomplete' ? 'scope.displayModel' : 'scope.ngModel'))) {
                        scope.validate = true;
                        scope.errorMessage = rules[i].error;
                        scope.errorModel = rules[i].error;
                        break;
                    } else {
                        if (scope.type === 'date') {
                            angular.element('#' + scope.textfieldId).removeClass('invalid');
                    }
                }
                }
                else {
                    if (!isFoundInvalid) {
                        if (typeof scope.ngModel != 'object')
                            var data = scope.ngModel;
                        else
                            var data = scope.displayModel;

                        RuleEngine.isValid(rules[i], function (e) {
                            scope.validate = true;
                            scope.errorMessage = e.errorData.error;
                            scope.errorModel = rules[i].error;
                            isFoundInvalid = true;
                        }, data);
                    }
                    else {
                        break;
                }
            }
        }
        }

        var validateRules = function (scope, ngModelCtrl, element, attrs) {
            if (typeof scope.readonly !== 'undefined' && scope.readonly == 'true') {
                scope.isActive = true;
                return;
        }

            if ((scope.type === 'text' || scope.type === 'area') && angular.isString(scope.ngModel)) {
                if (attrs.noTrim == 'true') {
                    //scope.ngModel = scope.ngModel;
                } else {
                    //scope.ngModel = scope.ngModel.trim();
                    var tempString = modal.getModal(scope, ngModelCtrl);
                    if (tempString.trim().length <= 0) {
                        modal.setModal(scope, tempString.trim(), ngModelCtrl);
                    } else {
                        modal.setModal(scope, tempString, ngModelCtrl);
                }
            }
                //ngModelCtrl.$setViewValue(scope.ngModel);
        }

            if ((scope.type == 'autocomplete' && scope.$eval(scope.isMandatory) && !scope.clienterror && (typeof scope.ngModel === 'undefined' || scope.displayModel == '' || scope.displayModel == null || scope.displayModel == undefined || scope.ngModel.id === 0)) || (scope.type != 'autocomplete' && scope.$eval(scope.isMandatory) && !scope.clienterror && (scope.ngModel + '' == '' || scope.ngModel == null || scope.ngModel == undefined))) {
                scope.validate = true;
                scope.errorMessage = $translate.instant("enterValueErrMsg");
                scope.errorModel = $translate.instant("enterValueErrMsg");

                return;
        }

            if (scope.clienterror) {
                scope.validate = true;
                scope.errorMessage = $translate.instant("clientErrMsg");
                scope.errorModel = $translate.instant("clientErrMsg");

                return;
        }

            if (scope.type == 'number' && !scope.infinite) {
                if (!scope.$eval(scope.isMandatory) && isNaN(scope.ngModel)) {
                    angular.element('#' + scope.textfieldId).val('');
                    return;
            }
                if (!scope.$eval(scope.isMandatory) && scope.ngModel === null) {
                    if (scope.rules) {
                        checkRule(scope, element);
                }
                    return;
            }
                //if (scope.$eval(scope.isMandatory) && isNaN(scope.ngModel) || (!scope.$eval(scope.isMandatory) && number == undefined)) {
                //    scope.validate = true;
                //    scope.errorMessage = $translate.instant('enterNumberErrMsg');
                //    scope.errorModel = $translate.instant('enterNumberErrMsg');
                //    return;
                //}

        }

            // CON-5898
            if (scope.type == 'date' && !scope.rules) {
                $('#' + scope.textfieldId).removeClass('invalid');
        }

            scope.validate = false;
            scope.errorMessage = null;
            scope.errorModel = null;

            if (scope.rules) {
                checkRule(scope, element);
        }
        };

        var debounce = function (func, wait, immediate) {
            var timeout;
            return function () {
                var context = this, args = arguments;
                var later = function () {
                    timeout = null;
                    if (!immediate) func.apply(context, args);
            };
                var callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) func.apply(context, args);
        };
        };

        var updateDateClass = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            if (scope.type == 'date') {
                if (!el.hasClass('datepicker')) {
                    el.addClass('datepicker');
            }
        }


        }
        var assignDateEvents = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            el.bind('keydown', scope.ngKeyDown);
            el.bind('keypress', scope.ngKeyPress);
            el.bind('keyup', scope.ngKeyUp);

        }

        var assignAutocompleteEvents = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            //el.bind('keydown', scope.ngKeyDown);
            //el.bind('keypress', scope.ngKeyPress);
            el.bind('keyup', debounce(scope.ngKeyUp, 250));
            el.on('input', debounce(scope.search, 250));

        }

            /*
              * This function is required to generate value attribute 
              * and useful when up/down key is pressed
              */
        var getFormattedData = function (data, scope) {
            if (!data || data.length == 0) {
                return [];
        }
            for (var i = 0; i < data.length; i++) {
                var str = scope.ndisplayFormat;
                try {
                    var parentSplit = str.split('{');
                    for (var j = 0; j < parentSplit.length; j++) {
                        var childSplit = parentSplit[j].split('}');
                        for (var k = 0; k < childSplit.length; k++) {
                            if (childSplit[k].indexOf('.') > 0 || data[i].hasOwnProperty(childSplit[k])) {
                                str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), eval('data[i].' + childSplit[k]));
                        }
                    }
                }
            }
                catch (e) { }
                data[i].value = str.trim();
        }
            return data;
        };
            /*
                *  Convert timestamp to UTC timestamp 
                */
        var toUTC = function (date) {
            return Date.UTC(
                date.getFullYear()
                , date.getMonth()
                , date.getDate()
                , date.getHours()
                , date.getMinutes()
                , date.getSeconds()
                , date.getMilliseconds()
            );
        };

        var getGridFormatToTimestamp = function (scope, stringdate) {
            var tmpModel = (stringdate + '').replace(new RegExp("/", "ig"), '');
            // tmpModel = tmpModel.split('+');
            // tmpModel = eval('new ' + (tmpModel.length > 1 ? ('Date("' + tmpModel.join('+') + '").getTime()') : tmpModel[0]));
            //if (scope.applyClientZone) {
            //    return moment(tmpModel).parseZone().format(scope.dateFormat);
            //}
            //else {
            return moment(tmpModel).format(scope.dateFormat);
            //}
        }

        var updateNgModel = function (scope, timestamp) {
            if (scope.timePicker === 'true') {
                scope.timePicker = true;
            }
            scope.dateModifiedbyUser = true;
            if (scope.timePicker == true) {
                if (scope.convertIntoUTC || timeZoneOffset != 20) {
                    // that means the date will be saved in UTC Format
                    scope.ngModel = moment(timestamp * 1000).add((timeZoneOffset * -60), 'minute').format('YYYY-MM-DDTHH:mm:ss');  //'\/Date(' + ((timestamp - (moment().zone() * 60)) * 1000) + ')\/';
                } else {
                    //This case is applied for Sourcing. because of the converstion happed before saving.
                    scope.ngModel = '\/Date(' + (timestamp * 1000) + ')\/';
            }
            }
            else {
                // hooking a fixed time to fix the flipping issues.
                // if (scope.convertIntoUTC) {
                //scope.ngModel = moment(timestamp * 1000).format('YYYY-MM-DD') + 'T12:00:00';//'\/Date(' + (((timestamp- (moment().zone() * 60)) * 1000) + 43200000) + ')\/';
                //}else
                //{
                //    scope.ngModel = '\/Date(' + ((timestamp * 1000) + 43200000) + ')\/';
                //}

                if (timeZoneOffset != 20) {
                    scope.ngModel = moment(timestamp * 1000).format('YYYY-MM-DD') + 'T12:00:00';//'\/Date(' + (((timestamp- (moment().zone() * 60)) * 1000) + 43200000) + ')\/';
                } else {
                    scope.ngModel = '\/Date(' + ((timestamp * 1000) + 43200000) + ')\/';
            }
        }
        }


        var createDatePicker = function (scope, element, ngModelCtrl) {
            setTimeout(function () {
                var picker = element.find('#' + scope.textfieldId);
                var callApplyFuncFlag = true;
                function setDateFormate(datePar) {
                    updateNgModel(scope, datePar.unix());
                    //ngModelCtrl.$setViewValue(scope.ngModel);
                    //scope.$apply();
                    $timeout(function () {
                        // if (scope.doesContainDateString) {

                        //} else {
                        //    if (scope.isValueDateObj) {
                        //        var tempDate = datePar.format();
                        //        scope.ngModel = new Date(tempDate);
                        //    } else {
                        //        scope.ngModel = (datePar.unix() * 1000);
                        //    }
                        //}
                        //ngModelCtrl.$setViewValue(scope.ngModel);
                        scope.isDateBlank = false;                        
                            if (angular.isFunction(scope.onDateChange)) {
                                scope.onDateChange({ $event: { date: scope.ngModel } });
                    }
                        
                    })
            }
                var minDate = angular.isDefined(scope.min) ? scope.doesContainDateString ? getGridFormatToTimestamp(scope.min) : moment(parseInt(scope.min)).format(scope.dateFormat) : '';
                var maxDate = angular.isDefined(scope.max) ? scope.doesContainDateString ? getGridFormatToTimestamp(scope.max) : moment(parseInt(scope.max)).format(scope.dateFormat) : '';

                var timePicker24Hour = angular.isDefined(scope.timePicker12Hour) ? false : true;
                var timePickerIncrement = angular.isDefined(scope.minuteIncrement) ? scope.$eval(scope.minuteIncrement) : 1;

                var pickerObj = picker.daterangepicker({
                    "singleDatePicker": true,
                    "showDropdowns": true,
                    "timePicker": scope.datetimePicker,
                    "timePicker24Hour": timePicker24Hour,
                    "timePickerIncrement": timePickerIncrement,
                    "autoApply": false,
                    "opens": "right",
                    "drops": "down",
                    "minDate": minDate,
                    "maxDate": maxDate,
                    "autoUpdateInput": false,
                    "locale": {
                        "format": scope.dateFormat,
                        'cancelLabel': $translate.instant('cancelBtn'),
                        'applyLabel': $translate.instant('applyBtn')
                }
                }, function (start, end, label) {
                    callApplyFuncFlag = false;
                    setDateFormate(start);
                }).bind("show.daterangepicker", function () { // callback on after open datepicker
                    setPreviousDate(pickerObj.element.val());
                    var pos = pickerObj.container.offset(),
                        wHeight = window.innerHeight,
                        srTop = $(window).scrollTop();
                    if ((pos.top + pickerObj.container.outerHeight() - srTop) > wHeight) { // if datepicker goes down then push it up
                        pickerObj.drops = "up";
                        pickerObj.move();
                    } else if (pos.top < srTop) { // else if datepicker goes up then push it down
                        pickerObj.drops = "down";
                        pickerObj.move();
                }
                }).data("daterangepicker");

                picker.on('apply.daterangepicker', function (ev, pickerObj) {
                    if (callApplyFuncFlag) {
                        setDateFormate(pickerObj.startDate)
                }
                    callApplyFuncFlag = true;
                    pickerObj.autoUpdateInput = true;
                    pickerObj.updateElement();
                    pickerObj.autoUpdateInput = false;
                    setPreviousDate(pickerObj.element.val());
                    setSelectedDate('\/Date(' + ((pickerObj.startDate.unix() * 1000) + 43200000) + ')\/');
                });

                if (scope.isDateBlank) {
                    picker.val('');
                    scope.ngModel = "";
                    // WS-2072
                    //ngModelCtrl.$setViewValue('');

            };
            });
        }

        var getDisplayData = function (data, scope) {
            if (data == undefined || data == null || data == '') {
                return '';
        }
            var str = scope.ndisplayFormat, evalResult, evaluatedKeysCounter = 0, nullKeysCounter = 0;
            try {
                var parentSplit = str.split('{');
                for (var j = 0; j < parentSplit.length; j++) {
                    var childSplit = parentSplit[j].split('}');
                    for (var k = 0; k < childSplit.length; k++) {
                        if (childSplit[k].indexOf('.') > 0 || data.hasOwnProperty(childSplit[k])) {
                            evalResult = eval('data.' + childSplit[k]);
                            evaluatedKeysCounter++;
                            if (evalResult == undefined || evalResult == null || (evalResult + '').trim().length == 0) {
                                evalResult = '';
                                nullKeysCounter++;
                        }
                            str = str.replace(new RegExp("{" + childSplit[k] + "}", "ig"), evalResult);
                    }
                }
            }
                if (evaluatedKeysCounter == nullKeysCounter) {
                    str = '';
            }
        }
            catch (e) { }
            return str === undefined || str === null || str === 'undefined' || str === 'null' ? '' : str;
        }

        function debounce(func, wait, immediate) {
            var timeout;
            return function () {
                var context = this, args = arguments;
                var later = function () {
                    timeout = null;
                    if (!immediate) func.apply(context, args);
                };
                var callNow = immediate && !timeout;
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
                if (callNow) func.apply(context, args);
            };
        };

        var assignSearchEvents = function (scope, element) {
            var el = element.find('#' + scope.textfieldId);
            el.on('input', debounce(scope.triggerSearchEvent, 250));
        }
        

        var service = {
                buildTemplate: buildTemplate,
                setActiveClass: setActiveClass,
                updateTextFieldClass: updateTextFieldClass,
                checkIfActive: checkIfActive,
                validateRules: validateRules,
                checkRule: checkRule,
                updateTextValue: updateTextValue,
                debounce: debounce,
                updateDateClass: updateDateClass,
                assignDateEvents: assignDateEvents,
                getFormattedData: getFormattedData,
                toUTC: toUTC,
                getGridFormatToTimestamp: getGridFormatToTimestamp,
                createDatePicker: createDatePicker,
                updateDateValue: updateDateValue,
                updateAutocompleteValue: updateAutocompleteValue,
                getDisplayData: getDisplayData,
                assignAutocompleteEvents: assignAutocompleteEvents,
                modal: modal,
                setSelectedDate: setSelectedDate,
                getSelectedDate: getSelectedDate,
                setPreviousDate: setPreviousDate,
                getPreviousDate: getPreviousDate,
                assignSearchEvents: assignSearchEvents
        };

        return service;
    }

    (function ($) {

        $.extend({

                debounce: function (fn, timeout, invokeAsap, ctx) {

                if (arguments.length == 3 && typeof invokeAsap != 'boolean') {
                    ctx = invokeAsap;
                    invokeAsap = false;
                }

                var timer;

                return function () {

                    var args = arguments;
                    ctx = ctx || this;

                    invokeAsap && !timer && fn.apply(ctx, args);

                    clearTimeout(timer);

                    timer = setTimeout(function () {
                        !invokeAsap && fn.apply(ctx, args);
                        timer = null;
                    }, timeout);

                };

        },

                throttle: function (fn, timeout, ctx) {

                var timer, args, needInvoke;

                return function () {

                    args = arguments;
                    needInvoke = true;
                    ctx = ctx || this;

                    if (!timer) {
                        (function () {
                            if (needInvoke) {
                                fn.apply(ctx, args);
                                needInvoke = false;
                                timer = setTimeout(arguments.callee, timeout);
                            }
                            else {
                                timer = null;
                        }
                        })();
                }

                };

        }

        });

    })(jQuery);
    angular.module('SMART2').directive('smartCharacterCount', [function () {
        return {
                restrict: 'A',
                link: function (scope, element, attrs) {
                if (parseInt(attrs.smartCharacterCount) > -1) {
                    element.attr('length', attrs.smartCharacterCount);
                    element.characterCounter();
                }
        }
    }
    }]);

})();